import Draw, {createBox, DrawEvent, GeometryFunction} from 'ol/interaction/Draw';
import Modify from 'ol/interaction/Modify';
import Snap from 'ol/interaction/Snap';
import {unByKey} from 'ol/Observable';
import {Collection, Feature} from 'ol';
import {Circle, Geometry, LineString, MultiPolygon, Point, Polygon} from 'ol/geom';
import VectorSource from 'ol/source/Vector';
import GIS from './app';
import {formatUnits} from "./general/function";
import {Type} from "ol/geom/Geometry";
import {EventsKey} from "ol/events";
import {GeometryValidationCallback, GetIDs, VertexCountCallback} from "./general/callback";
import {StyleFunction, StyleLike} from "ol/style/Style";
import {DrawFeaturesOptions, DrawOptionsType} from "./configuration/edit";
import {SliverThresholds, SpikeThresholds} from "./configuration/topology";
import {FlatStyle} from "ol/style/flat";
import {primaryAction, shiftKeyOnly, singleClick} from "ol/events/condition";
import {getFeaturesFromSource, getIdProperty, highestIntegerId} from "./function/layer";
import {pointSegmentDistance, ringSelfIntersects, squaredDistance} from "./function/topology";
import {
    EPSGOptions,
    GetIdPropertyOptions,
    IDs,
    ModifyMode,
    ModifyOptions,
    ModifyRejectedCallback,
    ModifyRejectionReason,
    VertexCountOptions
} from "./general/options.ts";
import {EditConfiguration} from "./configuration/map.ts";
import {FeatureLike} from "ol/Feature";
import {createOlStyle} from "./style.ts";
import {SymbolDefinition} from "./configuration/style.ts";
import {ProjectionLike} from "ol/proj";
import {LayerType} from "./configuration/layer.ts";
import {MapBrowserEvent} from "ol";

class Edit {
    private readonly GIS: GIS;
    private editConfiguration: EditConfiguration | undefined;
    private olDrawInteraction?: Draw;
    private olModifyInteraction?: Modify;
    private olSnapInteraction: Snap[] = [];
    private style: StyleLike | FlatStyle | undefined;

    //Draw
    private idProperty: string = 'id';
    private increment: number = 0;
    private incrementId: boolean = false;
    private addFeatureListener: EventsKey | undefined;
    private source: VectorSource | undefined;
    private single = false;
    private type: Type | 'Rectangle' | undefined = undefined;
    private showMeasurement = true;
    private stopClick: boolean = false;
    // Generalization tolerance in screen pixels for the finished sketch
    // (see DrawFeaturesOptions.generalize). `false` disables generalization.
    private generalizeTolerancePixels: number | false = false;
    private static readonly defaultGeneralizeTolerancePixels = 3;
    private modifyFeatures = new Collection<Feature>();
    private dependencyListenerKeys: any[] = [];

    //Right-click vertex delete
    private deleteOnRightClick: boolean = false;
    private suppressContextMenuHandler: ((e: Event) => void) | undefined;

    //Delete-key vertex delete (when hovering a vertex)
    private deleteVertexOnHoverHandler: ((event: KeyboardEvent) => void) | undefined;

    //Topology helpers
    private edgeScale = 1e6;
    private edgeIndex = new Map<string, Array<{ feature: Feature; ring: number; i: number }>>();
    private preModifySnapshot = new Map<Feature, number[][][]>();
    private rollbackSnapshot = new Map<Feature, number[][][]>();
    private featureLayerMap = new WeakMap<Feature, string>();
    // Measured features already wired for automatic overlay recalculation — the
    // `change:geometry` listener is attached at most once per feature.
    private autoMeasurementFeatures = new WeakSet<Feature>();
    private currentEditedLayerName?: string;
    // Per-layer id property overrides for topology reporting (from the
    // `neighborAttributes` modify option). Maps a layer name to the property
    // whose value identifies that layer's features in the callback result;
    // layers absent here fall back to the global {@link idProperty}.
    private layerIdProperties: Record<string, string> = {};
    private topologySources: Array<{ source: VectorSource<Feature>, layer: string }> = [];
    private touchedIdsByLayer = new Map<string, Set<string | number>>();
    private sketchAnchor: number[] | null = null;

    //Self-intersection guard (see modify option `preventSelfIntersection`).
    //Enabled by default; `modify()` sets the effective value per session.
    private preventSelfIntersection: boolean = true;
    // Geometry `change` listeners attached to the dragged features for the
    // duration of a modify gesture; drive the live invalid-edit feedback.
    private selfIntersectionListenerKeys: EventsKey[] = [];
    // Features that moved together in the current gesture (the edited feature
    // plus any topology-connected neighbors OpenLayers drags with it). Used
    // to evaluate validity across the whole gesture and to revert them all
    // back to their per-gesture origin when the gesture is rejected.
    private gestureFeatures: Feature[] = [];
    // The features the user is DIRECTLY editing (the targets passed to
    // `modify()`), as opposed to neighbors OpenLayers drags along because they
    // share a vertex. Used by {@link coordinateOnPrimaryBoundary} to restrict
    // neighbor grabs to the shared boundary (see
    // {@link restrictNeighborEditsToSharedBoundary}).
    private primaryEditFeatures: Feature[] = [];
    // The full set of features a topology-guarded session governs: the primary
    // (directly-edited) features plus every neighbor found to touch them
    // (shared vertex / shared edge / T-node). The topological guard may adjust
    // any of these — including a neighbor OpenLayers never dragged directly —
    // so at modifyend their geometry is compared against the gesture-start
    // snapshot and every changed one is reported through the edit callback.
    private guardedFeatures: Feature[] = [];
    // When true (topology-guarded sessions), a modify grab is only allowed on
    // or near a PRIMARY feature's boundary. This keeps a dragged-along
    // neighbor editable exactly where it overlaps the edited feature (shared
    // vertices / shared edges) while making its non-shared "far" vertices
    // ungrabbable — so the shared border can be adjusted but the neighbor's
    // own shape cannot be reworked through the edited feature's session.
    private restrictNeighborEditsToSharedBoundary: boolean = false;
    // Snap collections that must exclude the currently-edited feature so a
    // vertex cannot snap to its OWN polygon boundary (see modify option
    // `snap`). Each mirrors a snap layer's source.
    private selfSnapCollections: Array<{ collection: Collection<Feature>; source: VectorSource<Feature> }> = [];
    // Features removed from the snap collections above for the duration of a
    // gesture, restored on modifyend.
    private selfSnapRemoved: Array<{ collection: Collection<Feature>; feature: Feature }> = [];
    // Live snap targets for the vertex currently being dragged: two Point
    // features at its immediate ring neighbours (left / right). Populated at
    // modifystart and cleared at modifyend, so the cursor magnetically snaps
    // onto an adjacent corner WHILE dragging — the live counterpart of the
    // on-release {@link snapDraggedVertexToAdjacentVertex} cleanup. Backed by a
    // dedicated Snap interaction (pushed onto {@link olSnapInteraction}).
    private adjacentSnapFeatures = new Collection<Feature>();

    // Live `addfeature` / `removefeature` subscriptions on every source a modify
    // session touches. Without them the session keeps editing and snapping to
    // features that no longer exist (see {@link attachModifySourceSync}).
    private modifySourceListenerKeys: EventsKey[] = [];
    // The Collection actually handed to the Modify interaction, so a removed
    // feature can be dropped from it. Undefined when the interaction is
    // source-backed — OpenLayers then tracks source changes itself.
    private modifyTargetCollection: Collection<Feature> | undefined;

    //Vertex-count reporting (for enabling/disabling an undo / "back" button while drawing)
    private vertexCountCallback?: VertexCountCallback;
    private lastReportedVertexCount: number = -1;

    //Geometry validation of a finished sketch (see the `validate` draw option).
    //`false` disables it; an object carries the thresholds to validate with.
    private validateDrawing: boolean | (SpikeThresholds & SliverThresholds & {tolerance?: number}) = false;
    private geometryValidationCallback?: GeometryValidationCallback;

    constructor(GIS: GIS) {
        this.GIS = GIS;
    }

    public init(editConfiguration: EditConfiguration) {
        this.editConfiguration = editConfiguration;
    }

    private addToMap(interaction: Draw | Modify | Snap | undefined): void {
        if (interaction) {
            this.GIS.Map.olObject!.addInteraction(interaction);
        }
    }

    private removeFromMap(interaction: Draw | Modify | Snap): void {
        this.GIS.Map.olObject!.removeInteraction(interaction);
    }

    /**
     * Commit editing of the layers. Stop drawing, modification etc.
     */
    public commit(): void {
        this.clearDependencyListeners();
        this.detachContextMenuSuppress();
        this.detachDeleteVertexOnHover();

        if (this.olDrawInteraction) {
            this.removeFromMap(this.olDrawInteraction);
            this.deactivateDrawListener();
            delete this.olDrawInteraction;
        }

        if (this.olModifyInteraction) {
            this.removeFromMap(this.olModifyInteraction);
            delete this.olModifyInteraction;
        }

        for (const snapInteraction of this.olSnapInteraction) {
            this.removeFromMap(snapInteraction);
        }
        this.olSnapInteraction = [];
        this.adjacentSnapFeatures.clear();

        this.clearSelfIntersectionListeners();
        this.detachModifySourceSync();
        this.modifyTargetCollection = undefined;
        this.setInvalidFeedback(false);
        this.restoreSelfSnapFeatures();
        this.selfSnapCollections = [];
        this.topologySources = [];
        this.gestureFeatures = [];
        this.primaryEditFeatures = [];
        this.guardedFeatures = [];
        this.restrictNeighborEditsToSharedBoundary = false;
        this.preventSelfIntersection = false;

        this.rollbackSnapshot.clear();
        this.topologyToleranceOverride = undefined;
        this.weldToleranceOverride = undefined;
        this.GIS.Overlay.hide();
    }

    /**
     * Rollback all geometry changes made during the current modify session.
     * Restores all affected features (including neighbors and linked layers)
     * to their state when `modify()` was called, then commits (ends editing).
     */
    public rollback(): void {
        if (this.rollbackSnapshot.size === 0) {
            console.warn('[Edit.rollback] No active modify session to rollback.');
            return;
        }

        for (const [feature, coordsSnapshot] of this.rollbackSnapshot) {
            const geometry = feature.getGeometry();
            if (!geometry) continue;

            if (geometry.getType() === 'Polygon') {
                (geometry as Polygon).setCoordinates(coordsSnapshot);
            } else if (geometry.getType() === 'MultiPolygon') {
                // Snapshot stored as a flat ring list — write back through
                // the structure-aware helper.
                this.setGeometryRings(geometry, this.cloneRings(coordsSnapshot));
            } else if (geometry.getType() === 'LineString') {
                (geometry as LineString).setCoordinates(coordsSnapshot[0]);
            } else if (geometry.getType() === 'Point') {
                (geometry as Point).setCoordinates(coordsSnapshot[0][0]);
            }
        }

        this.commit();
    }

    private captureRollbackSnapshot(source: VectorSource<Feature>): void {
        for (const feature of source.getFeatures()) {
            if (this.rollbackSnapshot.has(feature)) continue;
            const geometry = feature.getGeometry();
            if (!geometry) continue;

            const polygonRings = this.getGeometryRings(geometry);
            if (polygonRings) {
                // Polygon or MultiPolygon — flat ring list.
                this.rollbackSnapshot.set(feature, this.cloneRings(polygonRings));
            } else if (geometry.getType() === 'LineString') {
                this.rollbackSnapshot.set(feature, [(geometry as LineString).getCoordinates().map(c => [...c])]);
            } else if (geometry.getType() === 'Point') {
                this.rollbackSnapshot.set(feature, [[(geometry as Point).getCoordinates().slice()]]);
            }
        }
    }

    /**
     * Snapshot a feature's gesture-start geometry into {@link preModifySnapshot}
     * (first writer wins, so an already-captured feature is left untouched).
     * The snapshot is the baseline the topology repair passes work against and
     * the reference modifyend uses to detect which features the guard changed.
     */
    private capturePreModifySnapshot(feature: Feature): void {
        if (this.preModifySnapshot.has(feature)) return;
        const geometry = feature.getGeometry();
        const polygonRings = this.getGeometryRings(geometry);
        if (polygonRings) {
            // Polygon or MultiPolygon — flat ring list, consistent with
            // getGeometryRings used by the repair routines.
            this.preModifySnapshot.set(feature, this.cloneRings(polygonRings));
        } else if (geometry && geometry.getType() === 'LineString') {
            this.preModifySnapshot.set(feature, [(geometry as LineString).getCoordinates().map(c => [...c])]);
        } else if (geometry && geometry.getType() === 'Point') {
            this.preModifySnapshot.set(feature, [[(geometry as Point).getCoordinates().slice()]]);
        }
    }

    /**
     * The full set of features to run the topology guard over at modifyend:
     * the features OpenLayers reports as modified, unioned with every feature
     * the guarded session governs (primary + touching neighbors). This ensures
     * a neighbor whose shared border must be re-aligned is harmonized and
     * change-checked even when OpenLayers never dragged it directly.
     */
    /**
     * Notify the caller that a modify gesture was rejected and rolled back.
     *
     * A rejected gesture is a NO-OP by design — the change callback must NOT
     * fire, because nothing changed. That silence is itself a problem: on screen
     * the vertex follows the cursor during the drag and then jumps back to where
     * it started, which is easily misread as "it snapped somewhere else" (e.g.
     * onto a neighbouring border) rather than "this position was refused". The
     * optional `onRejected` callback closes that gap so an app can surface the
     * reason instead of leaving the user guessing.
     *
     * The reported ids use the same per-layer resolution as the change callback,
     * so a rejection can be matched against the features the caller knows about.
     */
    private reportRejectedGesture(
        options: ModifyOptions | undefined,
        features: Feature[],
        reason: ModifyRejectionReason = 'selfIntersection'
    ): void {
        const onRejected = (options as any)?.onRejected as ModifyRejectedCallback | undefined;
        if (typeof onRejected !== 'function') return;

        const idsByLayer: Record<string, (string | number)[]> = {};
        for (const feature of features) {
            const layerName = this.resolveFeatureLayer(feature);
            const id = this.featureIdValue(feature, layerName);
            if (id === undefined) continue;
            (idsByLayer[layerName] ??= []).push(id);
        }

        try {
            onRejected({reason, ids: idsByLayer});
        } catch (error) {
            console.warn('[Edit.modify] onRejected callback threw:', error);
        }
    }

    private collectGuardedFeatures(modifiedFeatures: Feature[]): Feature[] {
        const combined: Feature[] = [...modifiedFeatures];
        for (const feature of this.guardedFeatures) {
            if (!combined.includes(feature)) combined.push(feature);
        }
        return combined;
    }

    /**
     * Resolve the layer name a feature belongs to for topology reporting.
     *
     * The neighbor-inclusion pass in {@link createModify} records every
     * guarded feature in {@link featureLayerMap}. As a safety net (so a
     * guard-changed neighbor is never mis-filed or dropped), fall back to the
     * topology sources and classify the feature by the source that owns it,
     * caching the result. Only returns 'unknown' when no source claims it.
     */
    private resolveFeatureLayer(feature: Feature): string {
        const mapped = this.featureLayerMap.get(feature);
        if (mapped) return mapped;
        for (const {source, layer} of this.topologySources) {
            const owns = typeof (source as any).hasFeature === 'function'
                ? (source as any).hasFeature(feature)
                : source.getFeatures().includes(feature);
            if (owns) {
                this.featureLayerMap.set(feature, layer);
                return layer;
            }
        }
        return this.currentEditedLayerName ?? 'unknown';
    }

    /**
     * The reporting identifier of a feature, resolved for the layer it belongs
     * to: the layer's configured id property (from `neighborAttributes`, or the
     * global `attribute`) first, then the OpenLayers feature-level id.
     *
     * A modify session has one global id property (the edited layer's
     * `attribute` option), but neighbor layers routinely key their features on
     * a different property — e.g. real-estate parcels on `'TUNNUS'`, which
     * `feature.get(idProperty)` would never see. The per-layer override plus
     * the feature-level-id fallback keep such a neighbor from being moved by
     * the guard yet silently dropped from the callback (id `undefined`).
     */
    private featureIdValue(feature: Feature, layer?: string): string | number | undefined {
        const property = (layer && this.layerIdProperties[layer]) || this.idProperty;
        const fromProperty = feature.get(property);
        if (fromProperty !== undefined && fromProperty !== null) return fromProperty;
        return feature.getId();
    }

    /**
     * Record a feature's ID under its layer in {@link touchedIdsByLayer} so the
     * topology-guarded edit callback reports it. Idempotent (backed by a Set).
     */
    private recordTouchedFeature(feature: Feature): void {
        const layer = this.resolveFeatureLayer(feature);
        const id = this.featureIdValue(feature, layer);
        if (id === undefined) return;
        if (!this.touchedIdsByLayer.has(layer)) {
            this.touchedIdsByLayer.set(layer, new Set());
        }
        this.touchedIdsByLayer.get(layer)!.add(id);
    }

    /**
     * True when a feature's polygon geometry differs from its gesture-start
     * snapshot — i.e. the topology guard (or the drag) changed its vertices or
     * borders during this gesture. Features without a baseline snapshot, or
     * whose geometry is not ring-based, are treated as unchanged.
     */
    private featureGeometryChangedSincePreModify(feature: Feature): boolean {
        const before = this.preModifySnapshot.get(feature);
        if (!before) return false;
        const after = this.getGeometryRings(feature.getGeometry());
        if (!after) return false;
        return !this.ringsEqual(before, after);
    }

    /**
     * Deep coordinate equality of two flat ring lists (as produced by
     * {@link getGeometryRings}). Compares ring count, vertex count and every
     * X/Y coordinate.
     */
    private ringsEqual(a: number[][][], b: number[][][]): boolean {
        if (a.length !== b.length) return false;
        for (let ringIndex = 0; ringIndex < a.length; ringIndex++) {
            const ringA = a[ringIndex];
            const ringB = b[ringIndex];
            if (ringA.length !== ringB.length) return false;
            for (let vertexIndex = 0; vertexIndex < ringA.length; vertexIndex++) {
                if (ringA[vertexIndex][0] !== ringB[vertexIndex][0] ||
                    ringA[vertexIndex][1] !== ringB[vertexIndex][1]) {
                    return false;
                }
            }
        }
        return true;
    }

    /**
     * The single segment of a polygon sketch that holds nothing but its first
     * point and the point following the cursor — the state right after the first
     * click, before a third point turns the sketch into a surface. Returns
     * `null` for every other polygon, which is measured as an area as before.
     *
     * OpenLayers closes the sketch ring, so this state is stored as
     * `[first, cursor, first]`: it encloses no area (0 m²) and its perimeter
     * walks the one segment there and back, reporting twice the real distance.
     * Neither number describes anything the user drew, so the caller measures
     * this segment as a length instead (see #229679).
     *
     * @param geometry - Polygon sketch geometry to inspect.
     * @returns The first point → cursor segment, or `null` if the polygon has more points.
     */
    private firstSketchSegment(geometry: Polygon): LineString | null {
        const ring = geometry.getCoordinates()[0] ?? [];
        if (ring.length !== 3) return null;
        // Guard against a genuine (non-closed) three-vertex ring: only the
        // closed [first, cursor, first] form is the first-segment sketch.
        if (ring[0][0] !== ring[2][0] || ring[0][1] !== ring[2][1]) return null;
        return new LineString([ring[0], ring[1]]);
    }

    private showTooltip(geometry: Geometry, value: string) {
        if (this.showMeasurement) {
            this.GIS.Overlay.show(geometry, value);
        }
    }

    private updateMeasurement(feature: Feature, start: boolean) {
        const geometry = feature.getGeometry();
        const type = geometry?.getType();
        if (geometry) {
            // Recalculation (not a live sketch): write the measurement values back as
            // feature properties from the current geometry and stop — this is the single
            // source of truth shared with GeoJSON operation results.
            if (!start) {
                const measurement = this.computeMeasurementProperties(geometry);
                if (measurement) {
                    feature.setProperties(measurement);
                    // Keep this measured feature's overlay in sync with any later
                    // geometry replacement (e.g. a geometric operation calling
                    // setGeometry) without a manual recalculation call.
                    this.enableAutoMeasurement(feature);
                }
                return;
            }
            // start === true \u2014 show the transient measurement tooltip for the live sketch.
            // A live sketch is always a single-part geometry (Polygon / LineString / Circle);
            // multi-part geometries only arrive via recalculation, handled above.
            switch (type) {
                case "Circle": {
                    const areaFormatted = formatUnits(this.GIS.Projection.measure(geometry as Circle, 'area'), 'area', 'label');
                    const perimeterFormatted = formatUnits(this.GIS.Projection.measure(geometry as Circle, 'perimeter'), 'length', 'label');
                    const radiusFormatted = formatUnits(this.GIS.Projection.measure(geometry as Circle, 'ground_radius'), 'length', 'label');
                    const circleMeasurementsWithIconsText = '<i class="fa-solid fa-square-dashed"></i> ' + areaFormatted
                        + '<br>' + '<i class="fa-regular fa-draw-square"></i> ' + perimeterFormatted
                        + '<br>' + '<i class="fa-regular fa-arrow-up-right"></i> ' + radiusFormatted;

                    this.showTooltip(geometry, circleMeasurementsWithIconsText);
                    // Make the geometric center visible while measuring and anchor the
                    // measurement label (area/radius) directly above this center point.
                    if (this.showMeasurement) {
                        const center = (geometry as Circle).getCenter();
                        if (center) {
                            this.GIS.Overlay.showCenterPoint(center);
                        }
                    }
                    break;
                }
                case "Polygon": {
                    // While the first segment is being drawn the sketch is not a surface
                    // yet, so area and perimeter would both lie (0 m² and the one segment
                    // counted twice). Label the real first-point-to-cursor distance until
                    // the sketch spans an actual surface (see #229679).
                    const firstSegment = this.firstSketchSegment(geometry as Polygon);
                    if (firstSegment) {
                        const distanceFormatted = formatUnits(this.GIS.Projection.measure(firstSegment, 'length'), 'length', 'label');
                        const distanceWithIconText = '<i class="fa-solid fa-ruler-horizontal"></i> ' + distanceFormatted;

                        this.showTooltip(geometry, distanceWithIconText);
                        break;
                    }

                    const areaFormatted = formatUnits(this.GIS.Projection.measure(geometry as Polygon, 'area'), 'area', 'label');
                    const perimeterFormatted = formatUnits(this.GIS.Projection.measure(geometry as Polygon, 'perimeter'), 'length', 'label');
                    const areaWithIconsText = '<i class="fa-solid fa-square-dashed"></i> ' + areaFormatted + '<br>' + '<i class="fa-regular fa-draw-square"></i> ' + perimeterFormatted;

                    this.showTooltip(geometry, areaWithIconsText);
                    break;
                }
                case "LineString": {
                    const lengthFormatted = formatUnits(this.GIS.Projection.measure(geometry as LineString, 'length'), 'length', 'label');
                    const lengthWithIconsText = '<i class="fa-solid fa-ruler-horizontal"></i> ' + lengthFormatted;

                    this.showTooltip(geometry, lengthWithIconsText);
                    break;
                }
            }
        }

    }

    /**
     * Whether a feature already carries measurement properties (area/border length,
     * length or radius) written by {@link updateMeasurement} — i.e. it was drawn or
     * edited as a measured feature and therefore has a measurement overlay/label.
     * @param feature - Feature to inspect.
     */
    private hasMeasurementProperties(feature: Feature): boolean {
        return this.hasMeasurementValues(feature.getProperties());
    }

    /**
     * Whether a plain attribute bag carries measurement values. Same test as
     * {@link hasMeasurementProperties}, but for the properties of a GeoJSON
     * result of a geometric operation, which is an attribute bag rather than a
     * {@link Feature}.
     * @param attributes - Feature attributes to inspect.
     */
    public hasMeasurementValues(attributes: {[key: string]: any} | null | undefined): boolean {
        if (!attributes) return false;
        return attributes.cfm_area_value !== undefined
            || attributes.cfm_length_value !== undefined
            || attributes.cfm_radius_value !== undefined;
    }

    /**
     * The properties {@link computeMeasurementProperties} writes, grouped by the
     * measurement kind they describe. A geometry type produces the groups that
     * make sense for it — a polygon has an area and a border length, a line only
     * a length, a circle a radius on top of its area — so the groups a result
     * does NOT produce are exactly the ones that would be left over from the
     * shape it was made from (see {@link obsoleteMeasurementProperties}).
     */
    private static readonly measurementPropertyGroups: {[group: string]: string[]} = {
        area: ['area', 'cfm_area_value', 'cfm_area_value_formatted', 'cfm_area_icon',
            'cfm_perimeter_value', 'cfm_perimeter_value_formatted', 'cfm_perimeter_icon'],
        length: ['length', 'cfm_length_value', 'cfm_length_value_formatted', 'cfm_length_icon'],
        radius: ['radius', 'cfm_radius_value', 'cfm_radius_value_formatted', 'cfm_radius_icon'],
    };

    /**
     * The measurement kinds {@link computeMeasurementProperties} produces for a
     * geometry type. Anything not listed here is not measured at all (e.g. a
     * `Point`), so nothing is claimed about its measurement properties.
     */
    private static readonly measurementGroupsByGeometry: {[type: string]: string[]} = {
        Circle: ['radius', 'area'],
        Polygon: ['area'],
        MultiPolygon: ['area'],
        LineString: ['length'],
        MultiLineString: ['length'],
    };

    /**
     * Measurement properties that do NOT belong to a geometry and are therefore stale
     * on it — the leftovers of a *different* kind of measurement, carried over from the
     * shape the geometry was derived from.
     *
     * A geometric operation copies the source feature's attributes onto its result and
     * then refreshes the values that apply to the result's own geometry. The values of
     * another kind cannot be refreshed, because the result has no such measurement: a
     * part cut out of a polygon that used to be a circle (circles are stored as polygons,
     * GeoJSON has no circle) is not a circle, so the inherited `cfm_radius_value` — and
     * the `radius` label, which spells out the WHOLE circle's area — would keep reporting
     * the pre-cut shape. Those properties are dropped instead of being left to describe a
     * geometry that no longer exists.
     *
     * Only a measurement that is actually there is reported: a group counts as carried
     * over when `attributes` holds its `cfm_<kind>_value`. This keeps the plainly named
     * label properties (`radius`, `length`, `area`) safe — a data attribute that happens
     * to be called `length` is not a leftover measurement and is never removed. A
     * geometry that is not measured at all yields an empty list, so an unmeasured result
     * never loses properties either.
     * @param geometry - Geometry of the operation result.
     * @param attributes - Attributes copied from the source feature.
     */
    public obsoleteMeasurementProperties(geometry: Geometry, attributes: {[key: string]: any}): string[] {
        const groups = Edit.measurementGroupsByGeometry[geometry.getType()];
        if (!groups) return [];

        return Object.keys(Edit.measurementPropertyGroups)
            .filter((group) => !groups.includes(group) && attributes['cfm_' + group + '_value'] !== undefined)
            .flatMap((group) => Edit.measurementPropertyGroups[group]);
    }

    /**
     * Computes the measurement values for a geometry as a plain object of feature
     * properties, mirroring what a measured drawing carries: area & border length for
     * polygons (`Polygon`/`MultiPolygon`), length for lines (`LineString`/`MultiLineString`)
     * and radius/area/perimeter for a `Circle`. Returns `null` for geometry types that are
     * not measured (e.g. `Point`).
     *
     * Values are measured in the map's view projection. This is the single source of truth
     * for measurement properties, used when writing them onto a live feature and when
     * refreshing the properties of a GeoJSON result of a geometric operation.
     * @param geometry - Geometry to measure.
     */
    public computeMeasurementProperties(geometry: Geometry): {[key: string]: string | number} | null {
        switch (geometry.getType()) {
            case "Circle": {
                const radiusValue = this.GIS.Projection.measure(geometry as Circle, 'ground_radius');
                const radiusFormatted = formatUnits(radiusValue, 'length', 'label');
                const areaValue = this.GIS.Projection.measure(geometry as Circle, 'area');
                const areaFormatted = formatUnits(areaValue, 'area', 'label');
                const perimeterValue = this.GIS.Projection.measure(geometry as Circle, 'perimeter');
                const perimeterFormatted = formatUnits(perimeterValue, 'length', 'label');
                return {
                    radius: 'A: ' + areaFormatted + ' P: ' + perimeterFormatted + ' R: ' + radiusFormatted,
                    cfm_radius_value: radiusValue,
                    cfm_radius_value_formatted: radiusFormatted,
                    cfm_radius_icon: "",
                    cfm_area_value: areaValue,
                    cfm_area_value_formatted: areaFormatted,
                    cfm_area_icon: "",
                    cfm_perimeter_value: perimeterValue,
                    cfm_perimeter_value_formatted: perimeterFormatted,
                    cfm_perimeter_icon: '',
                };
            }
            case "Polygon":
            case "MultiPolygon": {
                const areaValue = this.GIS.Projection.measure(geometry as Polygon, 'area');
                const areaFormatted = formatUnits(areaValue, 'area', 'label');
                const perimeterValue = this.GIS.Projection.measure(geometry as Polygon, 'perimeter');
                const perimeterFormatted = formatUnits(perimeterValue, 'length', 'label');
                return {
                    area: 'A: ' + areaFormatted + ' P: ' + perimeterFormatted,
                    cfm_area_value: areaValue,
                    cfm_area_value_formatted: areaFormatted,
                    cfm_area_icon: "",
                    cfm_perimeter_value: perimeterValue,
                    cfm_perimeter_value_formatted: perimeterFormatted,
                    cfm_perimeter_icon: '',
                };
            }
            case "LineString":
            case "MultiLineString": {
                const lengthValue = this.GIS.Projection.measure(geometry as LineString, 'length');
                const lengthFormatted = formatUnits(lengthValue, 'length', 'label');
                return {
                    length: 'L: ' + lengthFormatted,
                    cfm_length_value: lengthValue,
                    cfm_length_value_formatted: lengthFormatted,
                    cfm_length_icon: "",
                };
            }
        }
        return null;
    }

    /**
     * Recalculates the measurement properties of a feature from its current geometry
     * and writes them back as feature properties (`cfm_area_value`,
     * `cfm_area_value_formatted`, `cfm_perimeter_value`, `cfm_length_value`, …) so the
     * area and border-length overlay/label reflects the current shape.
     *
     * Call this after a geometric operation (intersect, union, difference, split, clip)
     * changes a feature's geometry. Features that do not already carry measurement
     * properties are left untouched, so measurements are never injected into features
     * that were not displaying them.
     * @example GIS.Edit.recalculateMeasurement(feature);
     * @param feature - Feature whose measurement properties should be refreshed.
     */
    public recalculateMeasurement(feature: Feature): void {
        if (!this.hasMeasurementProperties(feature)) return;
        this.updateMeasurement(feature, false);
    }

    /**
     * Wires a feature so its area / border-length overlay recalculates
     * automatically whenever its geometry is replaced — e.g. when a geometric
     * operation (intersect, union, difference, clip, split) calls `setGeometry`.
     * Callers therefore never need to refresh the measurement manually after
     * changing a feature's geometry.
     *
     * The listener delegates to {@link recalculateMeasurement}, so only features
     * that already carry measurement properties are recomputed — plain features
     * are never given measurements they never displayed. Idempotent: the
     * `change:geometry` listener is attached at most once per feature.
     * @param feature - Feature whose measurements should track its geometry.
     */
    public enableAutoMeasurement(feature: Feature): void {
        if (this.autoMeasurementFeatures.has(feature)) return;
        this.autoMeasurementFeatures.add(feature);
        feature.on('change:geometry', () => this.recalculateMeasurement(feature));
    }

    /**
     * Snapping mainly used for feature creation or modification.
     * Snaps the pointer/vertex to one or more defined layers.
     * @example GIS.Edit.snap('polygon');
     * @example GIS.Edit.snap(['polygon', 'boundary']);
     * @param name - Layer name or array of layer names to snap to. Each layer should be present in the map.
     * @throws No such layer in the map.
     */
    public snap(name: string | string[]) {
        const layerNames = Array.isArray(name) ? name : [name];

        for (const layerName of layerNames) {
            const vector = this.GIS.Layer.getLayer(layerName);
            const source = vector.getSource();
            const snapInteraction = new Snap({source: source});
            this.olSnapInteraction.push(snapInteraction);
            this.addToMap(snapInteraction);
        }
    }

    /**
     * Start modification of a layer.
     * The layer should be present in the map.
     * @param name - The layer should be present in the map.
     * @param ids - Array of feature IDs to modify. If empty, all features in the layer will be modified.
     * @param options - Options object. `attribute` sets the ID property
     *   (default 'id'). `mode` controls the delete-click condition and accepts
     *   either a single {@link ModifyMode} value or an array combining
     *   several (e.g. `['deleteSingleClick', 'deleteRightClick']`).
     * @param callback Returns on 'modifyend' event feature IDs.
     * @example GIS.Edit.modify('point')
     * @example GIS.Edit.modify('line')
     * @example GIS.Edit.modify('polygon')
     * @example GIS.Edit.modify('polygon', undefined, { mode: 'deleteRightClick' })
     * @example GIS.Edit.modify('polygon', undefined, { mode: ['deleteSingleClick', 'deleteRightClick'] })
     * @throws No such layer in the map.
     * @throws Layer cannot be modified.
     * @throws Cannot modify empty layer.
     */
    public modify(
        name: string | string[],
        ids?: IDs,
        options?: GetIdPropertyOptions & ModifyOptions,
        callback?: GetIDs
    ) {
        const layerNames = Array.isArray(name) ? name : [name];
        const primaryName = layerNames[0];

        const vector = this.GIS.Layer.getLayer(primaryName);
        if (vector.get('modify') !== true) throw new Error('Layer cannot be modified.');

        const source = vector.getSource() as VectorSource<Feature>;
        const idProperty = getIdProperty(options);

        this.idProperty = idProperty;
        this.layerIdProperties = (options as any)?.neighborAttributes ?? {};

        this.commit();

        // When multiple layers are specified, automatically link them
        if (layerNames.length > 1) {
            const existingLinked = options?.linkedLayers ?? [];
            const additionalLinked = layerNames.slice(1).filter(l => !existingLinked.includes(l));
            options = { ...options, linkedLayers: [...existingLinked, ...additionalLinked] };
        }

        this.currentEditedLayerName = primaryName;
        const modeOption = options?.mode;
        const modes: ModifyMode[] = Array.isArray(modeOption)
            ? modeOption
            : (modeOption ? [modeOption] : ['default']);
        this.deleteOnRightClick = modes.includes('deleteRightClick');
        const deleteVertexOnHover = modes.includes('deleteVertexOnHover');

        // On by default — a vertex edit must never leave the polygon self-
        // intersecting. Disable explicitly with `preventSelfIntersection: false`.
        this.preventSelfIntersection = (options as any)?.preventSelfIntersection !== false;

        const useWholeSource = !ids || (Array.isArray(ids) && ids.length === 0);

        const topologyGuardEnabled = (options as any)?.topologyGuard === true;
        if (topologyGuardEnabled) {
            if ((options as any)?.edgeScale) this.edgeScale = (options as any).edgeScale;
            this.topologyToleranceOverride = (options as any)?.topologyTolerance;
            this.weldToleranceOverride = (options as any)?.weldTolerance;

            this.topologySources = [{source: source as VectorSource<Feature>, layer: primaryName}];

            const neighborNames = (options as any)?.neighbors as string[] | undefined;
            if (neighborNames?.length) {
                for (const neighborName of neighborNames) {
                    try {
                        const lyr = this.GIS.Layer.getLayer(neighborName);
                        const src = lyr.getSource() as VectorSource<Feature>;
                        this.topologySources.push({source: src, layer: neighborName});
                    } catch (error) {
                        console.warn(`Could not add neighbor layer ${neighborName}:`, error);
                    }
                }
            }

            if ((options as any)?.edgeScale == null) {
                const view = this.GIS.Map.olObject?.getView();
                const projection = view?.getProjection();
                const units = projection?.getUnits();

                if (units === 'm') {
                    this.edgeScale = 1000;
                } else if (units === 'degrees') {
                    this.edgeScale = 1e6;
                } else {
                    console.warn('[Edit] Unknown projection units, using default edgeScale=1e6', {units});
                    this.edgeScale = 1e6;
                }
            }

            this.rebuildEdgeIndex(this.topologySources);
        }

        if (useWholeSource) {
            if (layerNames.length > 1) {
                this.modifyFeatures.clear();
                for (const layerName of layerNames) {
                    try {
                        const layerSource = this.GIS.Layer.getLayer(layerName).getSource() as VectorSource<Feature>;
                        this.modifyFeatures.extend(layerSource.getFeatures());
                    } catch (error) {
                        console.warn(`[Edit.modify] Could not access layer "${layerName}":`, error);
                    }
                }
                if (this.modifyFeatures.getLength() === 0) throw new Error('Cannot modify empty layers.');
                this.createModify({features: this.modifyFeatures}, options as ModifyOptions, callback);
            } else {
                const all = source.getFeatures();
                if (all.length === 0) throw new Error('Cannot modify empty layer.');
                this.createModify({source}, options as ModifyOptions, callback);
            }
        } else {
            const found = getFeaturesFromSource(source, idProperty, ids);
            if (found.length === 0) throw new Error(`No features found by ${idProperty}.`);
            this.modifyFeatures.clear();
            this.modifyFeatures.extend(found);
            this.createModify({features: this.modifyFeatures}, options as ModifyOptions, callback);
        }

        this.addToMap(this.olModifyInteraction);

        // Live cursor snapping onto the dragged vertex's adjacent corners.
        // Added AFTER the Modify interaction so Snap adjusts the coordinate
        // first. Its target collection stays empty until a drag begins.
        this.setupAdjacentVertexSnap();

        if (options?.snap) {
            const editedLayerNames = new Set<string>(layerNames);
            this.setupModifySnap(options.snap, editedLayerNames);
        }

        if (this.deleteOnRightClick) {
            this.attachContextMenuSuppress();
        }

        if (deleteVertexOnHover) {
            this.attachDeleteVertexOnHover(callback, topologyGuardEnabled);
        }

        // Store initial geometry state for all affected features (for rollback)
        this.rollbackSnapshot.clear();
        this.captureRollbackSnapshot(source);
        if (layerNames.length > 1) {
            for (const layerName of layerNames.slice(1)) {
                try {
                    const layerSource = this.GIS.Layer.getLayer(layerName).getSource() as VectorSource<Feature>;
                    this.captureRollbackSnapshot(layerSource);
                } catch (error) {
                    console.warn(`[Edit.modify] Could not snapshot layer "${layerName}" for rollback:`, error);
                }
            }
        }
        const linkedLayers = options?.linkedLayers;
        if (linkedLayers?.length) {
            for (const layerName of linkedLayers) {
                try {
                    const layerSource = this.GIS.Layer.getLayer(layerName).getSource() as VectorSource<Feature>;
                    this.captureRollbackSnapshot(layerSource);
                } catch (error) {
                    console.warn(`[Edit.modify] Could not snapshot linked layer "${layerName}" for rollback:`, error);
                }
            }
        }
        const neighborNames = options?.neighbors;
        if (neighborNames?.length) {
            for (const neighborName of neighborNames) {
                try {
                    const layerSource = this.GIS.Layer.getLayer(neighborName).getSource() as VectorSource<Feature>;
                    this.captureRollbackSnapshot(layerSource);
                } catch (error) {
                    console.warn(`[Edit.modify] Could not snapshot neighbor layer "${neighborName}" for rollback:`, error);
                }
            }
        }

        // Follow every layer this session touches, so removing a feature (e.g.
        // GIS.Layer.removeFeatureByID) does not leave an editable/snappable
        // ghost at the place it used to occupy.
        const watchedLayerNames = new Set<string>(layerNames);
        for (const neighborName of neighborNames ?? []) watchedLayerNames.add(neighborName);
        for (const linkedName of linkedLayers ?? []) watchedLayerNames.add(linkedName);
        const snapOption = options?.snap;
        if (snapOption) {
            for (const snapName of Array.isArray(snapOption) ? snapOption : [snapOption]) {
                watchedLayerNames.add(snapName);
            }
        }
        this.attachModifySourceSync(watchedLayerNames);
    }

    private activeVertexInfo = new WeakMap<Feature, {
        type: 'existing' | 'inserted',
        ring: number,
        vertexIndex?: number,
        oldEdge?: { a: number[]; b: number[] }
    }>();

    /**
     * Squared distance from a point to a line segment, together with the
     * projection of the point onto the segment and the normalized segment
     * parameter (0 = segmentStart, 1 = segmentEnd, clamped). Shared with the
     * topology validator — see `src/function/topology`.
     */
    private pointSegmentsDistance(point: number[], segmentStart: number[], segmentEnd: number[]) {
        return pointSegmentDistance(point, segmentStart, segmentEnd);
    }

    private mapToleranceFromPixels(pixels: number) {
        const view = this.GIS.Map.olObject!.getView();
        const resolution = view.getResolution() ?? 1;
        return pixels * resolution;
    }

    /**
     * True when `coordinate` lies on (within `toleranceSquared` of) the
     * boundary of any feature the user is DIRECTLY editing
     * ({@link primaryEditFeatures}). Used to gate modify grabs in topology-
     * guarded sessions: a shared vertex or shared edge of a dragged-along
     * neighbor is coincident with the primary boundary and stays grabbable,
     * whereas the neighbor's non-shared "far" vertices are not. Returns false
     * when there are no primary features to test against.
     */
    private coordinateOnPrimaryBoundary(coordinate: number[], toleranceSquared: number): boolean {
        for (const feature of this.primaryEditFeatures) {
            const rings = this.getGeometryRings(feature.getGeometry());
            if (!rings) continue;
            for (const ring of rings) {
                for (let vertexIndex = 0; vertexIndex < ring.length - 1; vertexIndex++) {
                    const {distanceSquared} =
                        this.pointSegmentsDistance(coordinate, ring[vertexIndex], ring[vertexIndex + 1]);
                    if (distanceSquared <= toleranceSquared) return true;
                }
            }
        }
        return false;
    }

    /**
     * Zoom-INDEPENDENT tolerance for topological identity checks, in map
     * units. Derived from `edgeScale` — the same quantization used for edge
     * keys (e.g. edgeScale=1000 on metric projections → 2 mm). Pixel-based
     * tolerances must never be used for topology decisions: zoomed out they
     * span kilometers (false vertex insertions → spikes), zoomed in they
     * miss genuine T-nodes. Can be overridden per session via the
     * `topologyTolerance` modify option (map units).
     */
    private topologyEpsilon(): number {
        return this.topologyToleranceOverride ?? 2 / this.edgeScale;
    }

    private topologyToleranceOverride: number | undefined;

    /**
     * Tolerance for WELDING nearly-coincident corner vertices of different
     * features into exactly coincident ones, in map units. Real-world and
     * hand-drawn data routinely store the topologically-same corner
     * centimetres to metres apart. Every topological mechanism (chain
     * harmonization, T-node proximity pass, modifyend repair) depends on
     * corner identity, so corners are welded first.
     *
     * The default deliberately mirrors what OpenLayers' Modify does
     * interactively: OL groups and drags together all vertices within its
     * 10-PIXEL tolerance at the current zoom, so corners that look (and
     * drag) like one vertex on screen must also be one corner for the
     * topology model — otherwise the user sees a single vertex move but
     * the boundary tears at the bend/midpoints. Hence the default is
     * `max(50 / edgeScale, 10 screen pixels in map units)`; the short-edge
     * guard in {@link weldNearCoincidentCorners} keeps zoomed-out sessions
     * from welding genuinely distinct corners. Override per session with
     * the `weldTolerance` modify option (map units, `0` disables welding)
     * for fully zoom-independent behavior.
     */
    private weldToleranceValue(): number {
        if (this.weldToleranceOverride !== undefined) return this.weldToleranceOverride;
        let pixelTolerance = 0;
        try {
            pixelTolerance = this.mapToleranceFromPixels(10);
        } catch {
            // No map view available (e.g. headless usage) — data-space only.
        }
        return Math.max(50 / this.edgeScale, pixelTolerance);
    }

    /**
     * The zoom-INDEPENDENT part of the weld tolerance, in map units.
     *
     * {@link weldToleranceValue} deliberately includes a 10-SCREEN-PIXEL
     * component so that corners OpenLayers already drags as ONE vertex are also
     * one corner for the topology model. That component is only meaningful for
     * pre-existing data noise between corners nobody moved. It must never apply
     * to a corner the user just placed: 10 px is tens of METRES at a zoomed-out
     * view, so a vertex intentionally dragged into open space (e.g. just beyond
     * a real-estate border, to be intersected back later) would be silently
     * yanked onto the nearest neighboring compartment's corner.
     *
     * An explicit `weldTolerance` override is already zoom-independent and is
     * therefore honoured exactly as configured.
     */
    private weldToleranceDataSpace(): number {
        return this.weldToleranceOverride ?? 50 / this.edgeScale;
    }

    /**
     * True when `coordinate` is absent from `feature`'s pre-gesture snapshot —
     * i.e. the user moved a vertex to (or inserted one at) this position during
     * the current modify gesture.
     *
     * Without a snapshot for the feature nothing counts as newly placed, so the
     * pre-edit harmonization at {@link modify} start — which runs before any
     * gesture exists and legitimately reconciles stored data noise — keeps the
     * full pixel-adaptive weld tolerance.
     */
    private coordinatePlacedThisGesture(feature: Feature, coordinate: number[]): boolean {
        const oldRings = this.preModifySnapshot.get(feature);
        if (!oldRings) return false;
        const coincidentSquared = 1e-12;
        for (const oldRing of oldRings) {
            for (const oldVertex of oldRing) {
                if (this.squaredEuclideanDistance(oldVertex, coordinate) <= coincidentSquared) return false;
            }
        }
        return true;
    }

    private weldToleranceOverride: number | undefined;

    private edgeKey(coordA: [number, number], coordB: [number, number], scale: number = this.edgeScale): string {
        const roundedAx = Math.round(coordA[0] * scale), roundedAy = Math.round(coordA[1] * scale);
        const roundedBx = Math.round(coordB[0] * scale), roundedBy = Math.round(coordB[1] * scale);
        return roundedAx < roundedBx || (roundedAx === roundedBx && roundedAy <= roundedBy)
            ? `${roundedAx},${roundedAy}|${roundedBx},${roundedBy}`
            : `${roundedBx},${roundedBy}|${roundedAx},${roundedAy}`;
    }

    private squaredEuclideanDistance(coordA: number[], coordB: number[]) {
        return squaredDistance(coordA, coordB);
    }

    private cloneRings(rings: number[][][]) {
        return rings.map(ring => ring.map(([coordX, coordY]) => [coordX, coordY]));
    }

    /**
     * True when a single closed ring is self-intersecting — a vertex on its own
     * non-incident edge, a pinch against a non-adjacent vertex, or a proper
     * bowtie crossing. The geometry maths is the shared, pure
     * {@link ringSelfIntersects} in `src/function/topology`; what this wrapper
     * adds is the tolerance a *live edit* has to work at:
     * {@link topologyEpsilon}, derived from the current view resolution.
     *
     * A snapped coordinate sits on the target edge only up to floating-point
     * noise, so an exact test detects such touches only intermittently and let a
     * self-intersecting drag through on some attempts.
     *
     * A touch is ALWAYS rejected, even when the offending vertex also lies on
     * another feature's boundary (e.g. a shared border snapped onto). There is
     * no valid simple-polygon configuration where a vertex belongs on its own
     * non-adjacent edge, so snapping an outer vertex onto the shared border of
     * a neighbor still corrupts the polygon and must be blocked.
     */
    private ringSelfIntersects(ring: number[][]): boolean {
        const tolerance = this.topologyEpsilon();

        // Collapse consecutive duplicates BEFORE testing. Geometry produced by
        // analysis operations (turf intersect / difference, see
        // GIS.Analysis.intersectFeaturesOfLayers) routinely carries consecutive
        // vertices a few NANOMETRES apart. Such a pair is a degenerate
        // zero-length edge, not a self-intersection — but left in place the
        // second vertex lies exactly on the edge ending at the first, which
        // the vertex-on-edge pass reports as a touch. That false positive is
        // severe: the feature then fails the check no matter what the user
        // does, so every gesture is reverted and the polygon becomes
        // permanently uneditable.
        // {@link mergeAdjacentCoincidentVertices} removes them for real; here
        // the check only needs to see through them.
        const working = this.collapseConsecutiveDuplicates(ring, tolerance * tolerance);
        if (working.length - 1 < 3) return false;

        return ringSelfIntersects(working, tolerance);
    }

    /**
     * Return a view of `ring` with consecutive coincident vertices collapsed,
     * preserving closure. Returns the input unchanged when nothing collapses, so
     * the common (clean) case allocates nothing extra.
     */
    private collapseConsecutiveDuplicates(ring: number[][], toleranceSquared: number): number[][] {
        if (ring.length < 2) return ring;

        const closed = this.squaredEuclideanDistance(ring[0], ring[ring.length - 1]) <= 1e-12;
        const corners = closed ? ring.slice(0, ring.length - 1) : ring.slice();

        const deduped: number[][] = [];
        for (const corner of corners) {
            const previous = deduped[deduped.length - 1];
            if (previous && this.squaredEuclideanDistance(previous, corner) <= toleranceSquared) continue;
            deduped.push(corner);
        }
        // The first and last corners may now be coincident across the wrap.
        while (deduped.length > 1 &&
            this.squaredEuclideanDistance(deduped[0], deduped[deduped.length - 1]) <= toleranceSquared) {
            deduped.pop();
        }

        if (deduped.length === corners.length) return ring;
        return closed ? [...deduped, [deduped[0][0], deduped[0][1]]] : deduped;
    }

    /** True when any ring of a raw ring array is self-intersecting. */
    private ringsSelfIntersect(rings: number[][][]): boolean {
        return rings.some(ring => this.ringSelfIntersects(ring));
    }

    /**
     * True when the geometry (Polygon or MultiPolygon) has any self-
     * intersecting linear ring. Non-polygon geometries are never considered
     * self-intersecting here. Each ring is evaluated independently, so
     * legitimately touching parts/holes are not falsely rejected.
     */
    private geometrySelfIntersects(geometry: Geometry | undefined): boolean {
        const rings = this.getGeometryRings(geometry);
        if (!rings) return false;
        return this.ringsSelfIntersect(rings);
    }

    /**
     * Collapse consecutive coincident vertices of a ring into one. When a
     * vertex is dragged (and snapped) ONTO its immediate neighbor the two
     * corners land on the same coordinate — the user's intent is to merge
     * them ("squeeze together", one vertex removed), not to keep a
     * zero-length edge. Only truly coincident corners are merged (snapping
     * makes them exactly equal), so genuinely distinct nearby vertices are
     * preserved. A ring is never reduced below a valid closed triangle.
     */
    private mergeAdjacentCoincidentVertices(features: Feature[]): void {
        const coincidentSquared = 1e-12;

        for (const feature of features) {
            const geometry = feature.getGeometry();
            const rings = this.getGeometryRings(geometry);
            if (!geometry || !rings) continue;

            let changed = false;
            const mergedRings = rings.map(ring => {
                const unique = ring.slice(0, ring.length - 1); // drop closing duplicate
                if (unique.length <= 3) return ring;

                const deduped: number[][] = [];
                for (const vertex of unique) {
                    const previous = deduped[deduped.length - 1];
                    if (previous && this.squaredEuclideanDistance(previous, vertex) <= coincidentSquared) continue;
                    deduped.push([vertex[0], vertex[1]]);
                }
                // Wrap-around: the first and last unique vertices may now coincide.
                while (deduped.length > 3 &&
                    this.squaredEuclideanDistance(deduped[0], deduped[deduped.length - 1]) <= coincidentSquared) {
                    deduped.pop();
                }

                if (deduped.length < 3 || deduped.length === unique.length) return ring;
                changed = true;
                return [...deduped, [deduped[0][0], deduped[0][1]]];
            });

            if (changed) this.setGeometryRings(geometry, mergedRings);
        }
    }

    /**
     * Zoom-dependent tolerance (map units) within which a dropped vertex snaps
     * onto its nearest immediate ring neighbour. Mirrors OpenLayers' own 10 px
     * snap feel, so a corner released close enough to look like it lands on the
     * neighbour on screen actually does. Falls back to `0` (no adjacent snap)
     * when there is no map view (headless usage).
     */
    private adjacentVertexSnapTolerance(): number {
        try {
            return this.mapToleranceFromPixels(10);
        } catch {
            return 0;
        }
    }

    /**
     * Snap a dragged vertex onto its nearest immediate ring neighbour (the
     * vertex on its left or right side).
     *
     * A vertex being edited can never snap to its OWN polygon boundary — the
     * edited feature is deliberately excluded from its own snap candidates
     * (see {@link excludeEditedFeaturesFromSelfSnap}) so a vertex cannot be
     * dropped onto a non-adjacent edge of the same ring (which would pinch or
     * split it). This restores the one self-snap that stays useful: dropping a
     * vertex onto the corner immediately before or after it, merging two
     * neighbouring corners into one.
     *
     * For every vertex the user MOVED in this gesture — a position present in
     * the current ring but absent from the pre-gesture snapshot — the distance
     * to each STATIONARY immediate neighbour (an existing corner that did not
     * move) is measured. When the NEARER such neighbour lies within `tolerance`
     * — but is not already coincident — the moved vertex is rewritten to that
     * neighbour's EXACT coordinate. The resulting duplicate corner is collapsed
     * to a single vertex by {@link mergeAdjacentCoincidentVertices}, so no
     * duplicate vertex remains on the polygon.
     *
     * Two safeguards keep this from merging corners the user did NOT intend to
     * drop together:
     * - Only a stationary neighbour is a snap target, so a segment drag (which
     *   moves BOTH endpoints of an edge) never collapses that edge.
     * - A zoom-independent relative guard (mirroring
     *   {@link weldNearCoincidentCorners}): the snap distance must stay below a
     *   fifth of the dragged vertex's OTHER edge and of the neighbour's far
     *   edge, so two genuinely distinct corners pulled apart are not welded.
     *
     * A ring is never snapped below a valid triangle.
     *
     * @returns true when at least one vertex was snapped.
     */
    private snapDraggedVertexToAdjacentVertex(features: Feature[], tolerance: number): boolean {
        if (tolerance <= 0) return false;
        const toleranceSquared = tolerance * tolerance;
        const coincidentSquared = 1e-12;
        let changed = false;

        for (const feature of features) {
            const geometry = feature.getGeometry();
            const rings = this.getGeometryRings(geometry);
            const oldRings = this.preModifySnapshot.get(feature);
            if (!geometry || !rings || !oldRings) continue;

            const isStationary = (oldRing: number[][], point: number[]): boolean =>
                oldRing.some(oldVertex =>
                    this.squaredEuclideanDistance(oldVertex, point) <= coincidentSquared);

            let featureChanged = false;

            for (let ringIndex = 0; ringIndex < rings.length; ringIndex++) {
                const ring = rings[ringIndex];
                const oldRing = oldRings[ringIndex];
                if (!oldRing) continue;

                const lastIndex = ring.length - 1; // closing duplicate == ring[0]
                // Need at least four distinct corners so collapsing one still
                // leaves a valid (triangle) ring.
                if (lastIndex < 4) continue;

                for (let vertexIndex = 0; vertexIndex < lastIndex; vertexIndex++) {
                    const vertex = ring[vertexIndex];

                    // Only vertices the user actually moved this gesture — a
                    // moved-to position is absent from the pre-gesture ring.
                    if (isStationary(oldRing, vertex)) continue;

                    const previousIndex = (vertexIndex - 1 + lastIndex) % lastIndex;
                    const nextIndex = (vertexIndex + 1) % lastIndex;

                    // Each candidate carries: the neighbour to snap ONTO, the
                    // dragged vertex's OTHER neighbour (opposite side) and the
                    // neighbour's own far neighbour — the latter two drive the
                    // relative guard below.
                    const candidates = [
                        {
                            coordinate: ring[previousIndex],
                            vertexOther: ring[nextIndex],
                            neighborFar: ring[(previousIndex - 1 + lastIndex) % lastIndex],
                        },
                        {
                            coordinate: ring[nextIndex],
                            vertexOther: ring[previousIndex],
                            neighborFar: ring[(nextIndex + 1) % lastIndex],
                        },
                    ].filter(candidate => isStationary(oldRing, candidate.coordinate));

                    let best: { coordinate: number[]; distanceSquared: number; guardSquared: number } | null = null;
                    for (const candidate of candidates) {
                        const distanceSquared = this.squaredEuclideanDistance(vertex, candidate.coordinate);
                        if (distanceSquared <= coincidentSquared) continue; // already there
                        if (distanceSquared > toleranceSquared) continue;    // out of magnet range

                        // Relative guard: never weld distinct corners. The snap
                        // distance must be below a fifth (0.2^2 = 0.04) of both
                        // the dragged vertex's opposite edge and the neighbour's
                        // far edge — genuine "dropped on top of" drops pass, a
                        // vertex parked partway along an edge does not.
                        const guardSquared = Math.min(
                            this.squaredEuclideanDistance(vertex, candidate.vertexOther),
                            this.squaredEuclideanDistance(candidate.coordinate, candidate.neighborFar)
                        ) * 0.04;
                        if (distanceSquared > guardSquared) continue;

                        if (!best || distanceSquared < best.distanceSquared) {
                            best = {coordinate: candidate.coordinate, distanceSquared, guardSquared};
                        }
                    }

                    if (!best) continue;

                    ring[vertexIndex] = [best.coordinate[0], best.coordinate[1]];
                    if (vertexIndex === 0) {
                        ring[lastIndex] = [best.coordinate[0], best.coordinate[1]];
                    }
                    featureChanged = true;
                    // One snap per ring is enough; the coincident-merge pass
                    // collapses the duplicate that this snap created.
                    break;
                }
            }

            if (featureChanged) {
                this.setGeometryRings(geometry, rings);
                changed = true;
            }
        }

        return changed;
    }

    /**
     * Toggle the invalid-edit visual feedback: a `gis-modify-invalid` CSS
     * class on the map viewport that consuming applications can style (e.g. a
     * red "not-allowed" cursor). Applied while a drag would create a self-
     * intersection, removed once the candidate is valid again.
     */
    private setInvalidFeedback(invalid: boolean): void {
        const viewport = this.GIS.Map.olObject?.getViewport();
        if (!viewport) return;
        viewport.classList.toggle('gis-modify-invalid', invalid);
    }

    private clearSelfIntersectionListeners(): void {
        for (const key of this.selfIntersectionListenerKeys) {
            unByKey(key);
        }
        this.selfIntersectionListenerKeys = [];
    }

    /**
     * Live invalid-edit feedback during a vertex drag. Runs on every geometry
     * `change` of any feature moving in the gesture. The `gis-modify-invalid`
     * class is shown whenever ANY of the connected features would end up self-
     * intersecting, so dragging a shared vertex flags the problem regardless
     * of which of the connected polygons folds over.
     */
    private refreshSelfIntersectionFeedback(): void {
        const invalid = this.gestureFeatures.some(feature =>
            this.geometrySelfIntersects(feature.getGeometry()));
        this.setInvalidFeedback(invalid);
    }

    /**
     * Restore a single feature's geometry from a per-gesture snapshot (flat
     * ring list for Polygon/MultiPolygon, as produced by {@link cloneRings}
     * over {@link getGeometryRings}). Mirrors the type handling in
     * {@link rollback}.
     */
    private restoreFeatureGeometry(feature: Feature, snapshot: number[][][]): void {
        const geometry = feature.getGeometry();
        if (!geometry) return;
        const type = geometry.getType();
        if (type === 'Polygon') {
            (geometry as Polygon).setCoordinates(this.cloneRings(snapshot));
        } else if (type === 'MultiPolygon') {
            this.setGeometryRings(geometry, this.cloneRings(snapshot));
        } else if (type === 'LineString') {
            (geometry as LineString).setCoordinates(snapshot[0].map(coordinate => [...coordinate]));
        } else if (type === 'Point') {
            (geometry as Point).setCoordinates([...snapshot[0][0]]);
        }
    }

    /**
     * Reject a whole gesture when it INTRODUCED a self-intersection on any
     * connected feature. Every feature that moved in the gesture — the edited
     * polygon AND any topology-connected neighbors OpenLayers dragged with it
     * — is restored to the position it had when THIS drag began (its
     * `modifystart` snapshot). Shared vertices therefore collapse back to
     * their single origin coordinate together, and because the snapshot is
     * per gesture, edits made earlier in the session are preserved. Returns
     * true when the gesture was rejected.
     *
     * Crucially the verdict is RELATIVE to the pre-gesture geometry, not an
     * absolute validity test. A polygon can arrive already non-simple — most
     * commonly straight out of an analysis operation such as
     * `GIS.Analysis.intersectFeaturesOfLayers` — and judging the result in
     * absolute terms froze such a feature permanently: every drag was reverted,
     * including the ones that would have repaired it, and the repair passes that
     * run after this check (welding, coincident-vertex merging) never got the
     * chance to execute because the rejection returns early. A gesture that
     * leaves an already-broken polygon no worse is therefore allowed through.
     */
    private enforceNoSelfIntersection(features: Feature[]): boolean {
        const offenders = features.filter(feature =>
            this.geometrySelfIntersects(feature.getGeometry()));
        if (!offenders.length) return false;

        const introducedByGesture = offenders.some(feature => {
            const snapshot = this.preModifySnapshot.get(feature);
            // No baseline to compare against — stay conservative and reject.
            if (!snapshot) return true;
            return !this.ringsSelfIntersect(snapshot);
        });
        if (!introducedByGesture) return false;

        for (const feature of features) {
            const snapshot = this.preModifySnapshot.get(feature);
            if (snapshot) this.restoreFeatureGeometry(feature, snapshot);
        }
        return true;
    }

    /**
     * Keep an ACTIVE modify session in step with its layers when features are
     * added to or removed from them mid-session.
     *
     * `modify()` snapshots its layers in two places: the Collection handed to the
     * Modify interaction (topology-guarded sessions build their own from the
     * primary features plus touching neighbors) and the per-layer snap mirrors
     * created by {@link setupModifySnap} for layers that are themselves edited.
     * Neither follows its source afterwards, so deleting a feature used to leave
     * a ghost behind: its vertices stayed grabbable and its edges stayed
     * magnetic at the place it used to occupy, and the topology guard kept
     * harmonizing against geometry no longer in the layer. Restarting `modify()`
     * was the only way out.
     *
     * Subscribing to the sources fixes that at the root, so the session stays
     * valid across `GIS.Layer.removeFeatureByID` and friends.
     */
    private attachModifySourceSync(layerNames: Iterable<string>): void {
        this.detachModifySourceSync();

        const watched = new Set<VectorSource<Feature>>();
        for (const layerName of layerNames) {
            let source: VectorSource<Feature> | undefined;
            try {
                source = this.GIS.Layer.getLayer(layerName).getSource() as VectorSource<Feature>;
            } catch {
                continue; // layer not in the map — nothing to keep in step
            }
            if (!source || watched.has(source)) continue;
            watched.add(source);

            const removeKey = source.on('removefeature', (event: any) => {
                const feature = event?.feature as Feature | undefined;
                if (feature) this.forgetFeatureFromSession(feature);
            }) as EventsKey;
            const addKey = source.on('addfeature', (event: any) => {
                const feature = event?.feature as Feature | undefined;
                if (feature) this.trackAddedFeatureInSession(feature, source!);
            }) as EventsKey;

            this.modifySourceListenerKeys.push(removeKey, addKey);
        }
    }

    private detachModifySourceSync(): void {
        for (const key of this.modifySourceListenerKeys) unByKey(key);
        this.modifySourceListenerKeys = [];
    }

    /**
     * Purge every trace of a feature that was just removed from its layer, so
     * the running session stops treating it as editable, snappable or guarded.
     */
    private forgetFeatureFromSession(feature: Feature): void {
        // Modify subscribes to its Collection, so removing the feature here also
        // purges it from the interaction's segment index — its vertices stop
        // being offered at the location it used to occupy.
        this.modifyTargetCollection?.remove(feature);
        if (this.modifyFeatures.getArray().includes(feature)) this.modifyFeatures.remove(feature);

        // Drop it as a snap target, and forget any pending self-snap restore
        // that would otherwise push it back on modifyend.
        for (const {collection} of this.selfSnapCollections) {
            if (collection.getArray().includes(feature)) collection.remove(feature);
        }
        this.selfSnapRemoved = this.selfSnapRemoved.filter(entry => entry.feature !== feature);

        this.guardedFeatures = this.guardedFeatures.filter(guarded => guarded !== feature);
        this.primaryEditFeatures = this.primaryEditFeatures.filter(primary => primary !== feature);
        this.gestureFeatures = this.gestureFeatures.filter(inGesture => inGesture !== feature);
        this.preModifySnapshot.delete(feature);
        this.rollbackSnapshot.delete(feature);

        if (this.topologySources.length) this.rebuildEdgeIndex(this.topologySources);
    }

    /**
     * Keep the snap mirrors true to their source when a feature is added
     * mid-session, so a freshly created polygon is immediately snappable.
     *
     * The Modify collection is deliberately NOT extended: what is editable stays
     * exactly what the caller passed to `modify()`.
     */
    private trackAddedFeatureInSession(feature: Feature, source: VectorSource<Feature>): void {
        for (const {collection, source: mirroredSource} of this.selfSnapCollections) {
            if (mirroredSource !== source) continue;
            if (!collection.getArray().includes(feature)) collection.push(feature);
        }

        if (this.topologySources.length) this.rebuildEdgeIndex(this.topologySources);
    }

    /**
     * Restore features previously removed from snap collections so a vertex
     * could not snap to its own polygon boundary.
     */
    private restoreSelfSnapFeatures(): void {
        for (const {collection, feature} of this.selfSnapRemoved) {
            if (!collection.getArray().includes(feature)) collection.push(feature);
        }
        this.selfSnapRemoved = [];
    }

    /**
     * Remove the feature(s) about to be dragged from every snap collection
     * whose source contains them, so a vertex cannot snap to a segment or
     * vertex of the SAME polygon. Other features stay snappable.
     */
    private excludeEditedFeaturesFromSelfSnap(features: Feature[]): void {
        if (!this.selfSnapCollections.length) return;
        for (const feature of features) {
            for (const {collection, source} of this.selfSnapCollections) {
                if (!source.getFeatures().includes(feature)) continue;
                if (collection.getArray().includes(feature)) {
                    collection.remove(feature);
                    this.selfSnapRemoved.push({collection, feature});
                }
            }
        }
    }

    /**
     * Set up snapping for a modify session. A snap layer that is also being
     * edited is backed by a features {@link Collection} (mirroring its
     * source) instead of the source directly, so the currently-dragged
     * feature can be excluded from its own snap candidates for the duration
     * of the gesture. Snapping to every OTHER feature — and to layers that
     * are not being edited — is unchanged.
     */
    /**
     * Create the dedicated Snap interaction that magnetically pulls the cursor
     * onto the dragged vertex's immediate ring neighbours. Its target
     * collection ({@link adjacentSnapFeatures}) is empty here and populated per
     * gesture at modifystart; the interaction is pushed onto
     * {@link olSnapInteraction} so {@link commit} tears it down with the rest.
     */
    private setupAdjacentVertexSnap(): void {
        this.adjacentSnapFeatures.clear();
        const snapInteraction = new Snap({features: this.adjacentSnapFeatures});
        this.olSnapInteraction.push(snapInteraction);
        this.addToMap(snapInteraction);
    }

    /**
     * Populate the live adjacent-vertex snap targets for the vertex the user
     * just grabbed, given the gesture's start coordinate.
     *
     * The grabbed vertex is found as the NEAREST committed vertex (across all
     * PRIMARY edit features, read from the pre-gesture snapshot) to `start`.
     * This is deliberately proximity-based rather than an exact match: OL
     * dispatches `modifystart` on the first pointer-drag, by which point the
     * pointer has already moved off the vertex, so `start` sits a little away
     * from the true corner — but the corner the user grabbed is still the
     * closest one. Its two immediate ring neighbours (left / right) are pushed
     * as Point snap targets, so the cursor magnetically merges onto an adjacent
     * corner while dragging. Only primary features are considered, so a
     * dragged-along neighbour never gets its own corners wired up.
     */
    private armAdjacentVertexSnap(start: number[] | undefined): void {
        if (!start) return;

        let best: { ring: number[][]; index: number; distanceSquared: number } | null = null;
        for (const feature of this.primaryEditFeatures) {
            const oldRings = this.preModifySnapshot.get(feature);
            if (!oldRings) continue;
            for (const ring of oldRings) {
                const lastIndex = ring.length - 1; // closing duplicate == ring[0]
                if (lastIndex < 3) continue;
                for (let vertexIndex = 0; vertexIndex < lastIndex; vertexIndex++) {
                    const distanceSquared = this.squaredEuclideanDistance(ring[vertexIndex], start);
                    if (!best || distanceSquared < best.distanceSquared) {
                        best = {ring, index: vertexIndex, distanceSquared};
                    }
                }
            }
        }
        if (!best) return;

        const lastIndex = best.ring.length - 1;
        const previous = best.ring[(best.index - 1 + lastIndex) % lastIndex];
        const next = best.ring[(best.index + 1) % lastIndex];
        for (const coordinate of [previous, next]) {
            this.adjacentSnapFeatures.push(new Feature(new Point([coordinate[0], coordinate[1]])));
        }
    }

    private setupModifySnap(name: string | string[], editedLayerNames: Set<string>): void {
        const layerNames = Array.isArray(name) ? name : [name];

        for (const layerName of layerNames) {
            const vector = this.GIS.Layer.getLayer(layerName);
            const source = vector.getSource() as VectorSource<Feature>;

            if (editedLayerNames.has(layerName)) {
                const collection = new Collection<Feature>(source.getFeatures().slice());
                const snapInteraction = new Snap({features: collection});
                this.selfSnapCollections.push({collection, source});
                this.olSnapInteraction.push(snapInteraction);
                this.addToMap(snapInteraction);
            } else {
                const snapInteraction = new Snap({source});
                this.olSnapInteraction.push(snapInteraction);
                this.addToMap(snapInteraction);
            }
        }
    }

    /**
     * Returns all linear rings of a Polygon OR MultiPolygon geometry as a
     * flat, LIVE array of rings (mutations to the rings can be written back
     * with {@link setGeometryRings}). Returns null for other geometry types.
     * Real-world layers (forest compartments, cadastre) frequently mix
     * Polygon and MultiPolygon — every topology routine must handle both.
     */
    private getGeometryRings(geometry: Geometry | undefined): number[][][] | null {
        if (!geometry) return null;
        const type = geometry.getType();
        if (type === 'Polygon') {
            return (geometry as Polygon).getCoordinates();
        }
        if (type === 'MultiPolygon') {
            const flat: number[][][] = [];
            for (const polygonRings of (geometry as MultiPolygon).getCoordinates()) {
                for (const ring of polygonRings) flat.push(ring);
            }
            return flat;
        }
        return null;
    }

    /**
     * Writes back rings previously obtained via {@link getGeometryRings}
     * (and possibly mutated in place) into the geometry. The flat ring list
     * is re-chunked for MultiPolygon using the current part/ring structure.
     */
    private setGeometryRings(geometry: Geometry, flatRings: number[][][]): void {
        const type = geometry.getType();
        if (type === 'Polygon') {
            (geometry as Polygon).setCoordinates(flatRings);
            return;
        }
        if (type === 'MultiPolygon') {
            const structure = (geometry as MultiPolygon).getCoordinates();
            const rebuilt: number[][][][] = [];
            let cursor = 0;
            for (const polygonRings of structure) {
                rebuilt.push(flatRings.slice(cursor, cursor + polygonRings.length));
                cursor += polygonRings.length;
            }
            (geometry as MultiPolygon).setCoordinates(rebuilt);
        }
    }

    /**
     * Weld nearly-coincident corner vertices of DIFFERENT features into
     * exactly coincident ones.
     *
     * Real layer data is rarely topologically exact: the "same" parcel
     * corner is often stored a few millimetres to centimetres apart in two
     * neighboring polygons. OpenLayers' Modify happily drags both (its
     * pixel tolerance is generous), so the user sees them as one vertex —
     * but every data-space topology mechanism in this module (shared-chain
     * harmonization, T-node proximity pass, modifyend repair) matches
     * corners with the strict topology epsilon and silently fails on such
     * data. The result is the classic torn boundary: the corner moves on
     * both polygons, but the midpoint chain is never synchronized.
     *
     * This pass therefore runs FIRST: any vertex of a feature lying within
     * the weld tolerance of an earlier feature's vertex is rewritten to
     * that exact coordinate (the first feature in the list — the edited
     * one — wins). A safety guard refuses welds that would collapse or
     * significantly distort a short edge: the weld distance must stay
     * below a fifth of the length of both edges adjacent to the vertex.
     *
     * The pixel-adaptive component of the tolerance applies ONLY between two
     * STATIONARY corners (see {@link weldToleranceDataSpace}): a corner the
     * user just placed in this gesture is welded only within the zoom-
     * independent data-space tolerance, so an intentional drag is never yanked
     * onto a neighbor's corner metres away.
     *
     * @returns true when at least one vertex has been rewritten.
     */
    private weldNearCoincidentCorners(features: Feature[]): boolean {
        const tolerance = this.weldToleranceValue();
        if (tolerance <= 0) return false;
        const toleranceSquared = tolerance * tolerance;
        // Reduced, zoom-independent limit for corners placed in this gesture.
        const placedTolerance = Math.min(this.weldToleranceDataSpace(), tolerance);
        const placedToleranceSquared = placedTolerance * placedTolerance;
        const coincidentSquared = 1e-12;
        let changed = false;

        // Canonical corner registry — first occurrence wins. `placed` marks a
        // corner the user moved/inserted during the current gesture.
        const canonicals: { coordinate: number[]; feature: Feature; placed: boolean }[] = [];

        for (const feature of features) {
            const geometry = feature.getGeometry();
            const rings = this.getGeometryRings(geometry);
            if (!geometry || !rings) continue;
            let featureChanged = false;

            for (const ring of rings) {
                const lastIndex = ring.length - 1; // closing duplicate
                if (lastIndex < 3) continue;

                for (let vertexIndex = 0; vertexIndex < lastIndex; vertexIndex++) {
                    const vertex = ring[vertexIndex];
                    const vertexPlaced = this.coordinatePlacedThisGesture(feature, vertex);

                    // Find the NEAREST canonical corner of another feature —
                    // first-within-tolerance would let a farther (guard-
                    // refused) corner shadow the true twin of this vertex.
                    let match: { coordinate: number[]; feature: Feature } | null = null;
                    let matchDistanceSquared = Infinity;
                    for (const canonical of canonicals) {
                        if (canonical.feature === feature) continue;
                        const candidateDistanceSquared = this.squaredEuclideanDistance(canonical.coordinate, vertex);
                        // Either side placed in this gesture → data-space limit
                        // only. The 10-pixel component exists to reconcile
                        // pre-existing data noise, not to re-glue a corner the
                        // user deliberately dragged away.
                        const limitSquared = (vertexPlaced || canonical.placed)
                            ? placedToleranceSquared
                            : toleranceSquared;
                        if (candidateDistanceSquared <= limitSquared && candidateDistanceSquared < matchDistanceSquared) {
                            match = canonical;
                            matchDistanceSquared = candidateDistanceSquared;
                        }
                    }

                    if (!match) {
                        canonicals.push({coordinate: [vertex[0], vertex[1]], feature, placed: vertexPlaced});
                        continue;
                    }

                    const weldDistanceSquared = matchDistanceSquared;
                    if (weldDistanceSquared <= coincidentSquared) continue; // already exact

                    // Guard: never collapse/distort short edges — the weld
                    // distance must stay below a FIFTH of both adjacent edge
                    // lengths (0.2^2 = 0.04 in squared space). Corners that
                    // are truly the same point have parcel-scale adjacent
                    // edges, so cm welds pass; genuine micro-edges don't.
                    const previousVertex = ring[(vertexIndex - 1 + lastIndex) % lastIndex];
                    const nextVertex = ring[(vertexIndex + 1) % lastIndex];
                    const shortestAdjacentSquared = Math.min(
                        this.squaredEuclideanDistance(vertex, previousVertex),
                        this.squaredEuclideanDistance(vertex, nextVertex));
                    if (weldDistanceSquared > shortestAdjacentSquared * 0.04) continue;

                    ring[vertexIndex] = [match.coordinate[0], match.coordinate[1]];
                    if (vertexIndex === 0) ring[lastIndex] = [match.coordinate[0], match.coordinate[1]];
                    featureChanged = true;
                }
            }

            if (featureChanged) {
                this.setGeometryRings(geometry, rings);
                changed = true;
            }
        }

        return changed;
    }

    /**
     * Harmonize shared boundary CHAINS between polygon features.
     *
     * Combinatorial complement to {@link harmonizeTopologyVertices}: instead
     * of requiring an absolute on-edge distance (which misses midpoints that
     * are a few centimetres off the mathematical segment in real data), it
     * detects that two features share a boundary by their segment ENDPOINTS:
     *
     * For every single segment A–B of the target polygon, if a source
     * polygon's ring contains vertices coincident with BOTH A and B, the
     * chain of source vertices walked between them is the detailed shared
     * boundary, and its vertices are inserted into the target segment.
     * Genuine BENDS are included — a shared boundary may bend arbitrarily
     * between its shared corners; of the two ring-walk directions the chain
     * deviating LEAST from the straight segment wins, and chains deviating
     * beyond the segment length (the way around the polygon) are rejected.
     *
     * This guarantees that grabbing an ENDPOINT of a shared edge drags an
     * identical vertex chain on both polygons, with no discontinuity at the
     * bend/midpoint vertices only one of them had.
     *
     * @returns true when at least one vertex has been inserted.
     */
    private harmonizeSharedBoundaryChains(features: Feature[]): boolean {
        const epsilon = this.topologyEpsilon();
        const epsilonSquared = epsilon * epsilon;
        let changed = false;

        for (const targetFeature of features) {
            const targetGeometry = targetFeature.getGeometry();
            const targetRings = this.getGeometryRings(targetGeometry);
            if (!targetGeometry || !targetRings) continue;
            let featureChanged = false;

            for (const targetRing of targetRings) {
                for (let segmentIndex = 0; segmentIndex < targetRing.length - 1; segmentIndex++) {
                    const segmentStart = targetRing[segmentIndex];
                    const segmentEnd = targetRing[segmentIndex + 1];
                    const segmentLengthSquared = this.squaredEuclideanDistance(segmentStart, segmentEnd);
                    if (segmentLengthSquared <= epsilonSquared) continue;

                    // A shared boundary may genuinely BEND between its two
                    // shared corners — the neighbor simply lacks the bend
                    // vertices. Chains are accepted as long as their maximum
                    // deviation from the straight segment stays below the
                    // segment length; the chain that goes the long way
                    // around the source polygon deviates far more and the
                    // closer of the two walk directions always wins.
                    const maxDeviationSquared = segmentLengthSquared;

                    const insertions = this.findSharedChainInsertions(
                        features, targetFeature, targetRing,
                        segmentStart, segmentEnd,
                        epsilonSquared, maxDeviationSquared
                    );

                    if (insertions.length) {
                        targetRing.splice(segmentIndex + 1, 0, ...insertions);
                        segmentIndex += insertions.length; // skip inserted vertices
                        featureChanged = true;
                    }
                }
            }

            if (featureChanged) {
                this.setGeometryRings(targetGeometry, targetRings);
                changed = true;
            }
        }

        return changed;
    }

    /**
     * For a single target segment A–B, find the vertex chain of another
     * feature whose ring passes through both A and B and return the chain
     * vertices missing from the target ring, in walk order from A to B.
     *
     * The source ring is walked from A to B in BOTH directions, yielding
     * two candidate chains. The shared boundary is the chain that deviates
     * least from the straight segment — the other direction goes the long
     * way around the source polygon. Genuine bends are accepted: a shared
     * boundary may bend arbitrarily between its shared corners as long as
     * its deviation stays below the segment length; the neighbor simply
     * lacks the bend vertices and receives them here.
     */
    private findSharedChainInsertions(
        features: Feature[],
        targetFeature: Feature,
        targetRing: number[][],
        segmentStart: number[],
        segmentEnd: number[],
        epsilonSquared: number,
        maxDeviationSquared: number
    ): number[][] {
        for (const sourceFeature of features) {
            if (sourceFeature === targetFeature) continue;
            const sourceRings = this.getGeometryRings(sourceFeature.getGeometry());
            if (!sourceRings) continue;

            for (const sourceRing of sourceRings) {
                const ringLength = sourceRing.length - 1; // committed vertices
                if (ringLength < 3) continue;

                let startIndex = -1;
                let endIndex = -1;
                for (let vertexIndex = 0; vertexIndex < ringLength; vertexIndex++) {
                    if (startIndex < 0 && this.squaredEuclideanDistance(sourceRing[vertexIndex], segmentStart) <= epsilonSquared) {
                        startIndex = vertexIndex;
                    }
                    if (endIndex < 0 && this.squaredEuclideanDistance(sourceRing[vertexIndex], segmentEnd) <= epsilonSquared) {
                        endIndex = vertexIndex;
                    }
                }
                if (startIndex < 0 || endIndex < 0 || startIndex === endIndex) continue;

                // Walk the ring from A to B in both directions and measure
                // how far each candidate chain deviates from the segment;
                // keep the closer qualifying one.
                let bestChain: number[][] | null = null;
                let bestDeviationSquared = Infinity;
                let sourceSharesDirectEdge = false;
                for (const step of [1, -1] as const) {
                    const chain: number[][] = [];
                    let index = (startIndex + step + ringLength) % ringLength;
                    let aborted = false;
                    while (index !== endIndex) {
                        chain.push(sourceRing[index]);
                        if (chain.length > ringLength) { aborted = true; break; }
                        index = (index + step + ringLength) % ringLength;
                    }
                    if (aborted) continue;
                    if (chain.length === 0) {
                        // A and B are ADJACENT in the source ring: the
                        // source's boundary between them IS the direct edge
                        // A–B, identical to the target segment — nothing to
                        // insert. The other walk direction is by construction
                        // the long way around the source polygon; treating it
                        // as a candidate would splice the source's entire
                        // outline into the target whenever the source is flat
                        // enough to pass the deviation cap.
                        sourceSharesDirectEdge = true;
                        break;
                    }

                    let deviationSquared = 0;
                    for (const vertex of chain) {
                        const {distanceSquared} = this.pointSegmentsDistance(vertex, segmentStart, segmentEnd);
                        if (distanceSquared > deviationSquared) deviationSquared = distanceSquared;
                    }
                    if (deviationSquared >= maxDeviationSquared) continue;
                    if (deviationSquared < bestDeviationSquared) {
                        bestDeviationSquared = deviationSquared;
                        bestChain = chain;
                    }
                }
                if (sourceSharesDirectEdge || !bestChain) continue;

                // Only vertices the target does not already share — kept in
                // walk order (A to B), which is the correct insertion order
                // for bent chains where projection order is unreliable.
                const missing = bestChain.filter(vertex =>
                    !targetRing.some(targetVertex =>
                        this.squaredEuclideanDistance(targetVertex, vertex) <= epsilonSquared));
                if (!missing.length) continue;

                return missing.map(vertex => [vertex[0], vertex[1]]);
            }
        }

        return [];
    }

    /**
     * Convert T-nodes into true shared vertices across the given polygon
     * features. A T-node is a vertex of one feature lying on the *interior*
     * of another feature's edge — OpenLayers' Modify only drags exactly
     * coincident vertices together, so without this step moving such a
     * vertex tears the topology apart (gap/overlap on the neighbor edge).
     *
     * For every vertex of every feature, if it sits within `tolerance` of a
     * foreign segment's interior, the exact vertex coordinate is inserted
     * into that segment. After harmonization the vertices are coincident and
     * Modify keeps them together natively.
     *
     * @returns true when at least one vertex has been inserted.
     */
    private harmonizeTopologyVertices(features: Feature[], toleranceMapUnits?: number): boolean {
        const tolerance = toleranceMapUnits ?? this.topologyEpsilon();
        const toleranceSquared = tolerance * tolerance;
        let changed = false;

        // Collect every committed vertex of every feature once (closing
        // coordinates excluded — they duplicate the first vertex).
        const allVertices: number[][] = [];
        for (const feature of features) {
            const featureRings = this.getGeometryRings(feature.getGeometry());
            if (!featureRings) continue;
            for (const ring of featureRings) {
                for (let vertexIndex = 0; vertexIndex < ring.length - 1; vertexIndex++) {
                    allVertices.push(ring[vertexIndex]);
                }
            }
        }

        for (const feature of features) {
            const geometry = feature.getGeometry();
            const rings = this.getGeometryRings(geometry);
            if (!geometry || !rings) continue;
            let featureChanged = false;

            for (let ringIndex = 0; ringIndex < rings.length; ringIndex++) {
                const ring = rings[ringIndex];
                for (let segmentIndex = 0; segmentIndex < ring.length - 1; segmentIndex++) {
                    const segmentStart = ring[segmentIndex];
                    const segmentEnd = ring[segmentIndex + 1];
                    const insertions: { coordinate: number[]; parameterT: number }[] = [];

                    for (const vertex of allVertices) {
                        // Skip vertices coincident with either endpoint —
                        // those are already shared (or this feature's own).
                        if (this.squaredEuclideanDistance(vertex, segmentStart) <= toleranceSquared) continue;
                        if (this.squaredEuclideanDistance(vertex, segmentEnd) <= toleranceSquared) continue;

                        const {distanceSquared, parameterT} = this.pointSegmentsDistance(vertex, segmentStart, segmentEnd);
                        if (distanceSquared <= toleranceSquared && parameterT > 0 && parameterT < 1) {
                            // De-duplicate multiple coincident foreign vertices.
                            const alreadyQueued = insertions.some(entry =>
                                this.squaredEuclideanDistance(entry.coordinate, vertex) <= toleranceSquared);
                            if (!alreadyQueued) {
                                // Insert the EXACT foreign coordinate so the
                                // vertices become identical, not just close.
                                insertions.push({coordinate: [vertex[0], vertex[1]], parameterT});
                            }
                        }
                    }

                    if (insertions.length) {
                        insertions.sort((a, b) => a.parameterT - b.parameterT);
                        ring.splice(segmentIndex + 1, 0, ...insertions.map(entry => entry.coordinate));
                        segmentIndex += insertions.length; // skip the vertices just inserted
                        featureChanged = true;
                    }
                }
            }

            if (featureChanged) {
                this.setGeometryRings(geometry, rings);
                changed = true;
            }
        }

        return changed;
    }

    /**
     * Repair T-node discontinuities created by a vertex move.
     *
     * Scenario: polygon 1 has a (stationary) vertex M lying on polygon 2's
     * edge A–B. The shared endpoint A is dragged to A'. OL moves A on both
     * polygons, so polygon 1's boundary follows A'–M–B while polygon 2's
     * edge becomes the straight line A'–B, cutting across M — a
     * discontinuity. Proximity-based harmonization cannot repair this,
     * because M no longer lies anywhere near the new straight edge.
     *
     * This repair therefore works against the PRE-MOVE snapshots taken at
     * `modifystart`: for every stationary vertex M of one feature that lay
     * on another feature's OLD edge whose endpoint(s) moved, M is inserted
     * into the corresponding NEW edge, restoring the shared boundary.
     *
     * @returns true when at least one vertex has been inserted.
     */
    private repairTNodeDiscontinuities(features: Feature[]): boolean {
        // Topological identity must be zoom-independent: use the data-space
        // epsilon (edgeScale quantization), NOT a pixel-based tolerance.
        // A pixel tolerance zoomed out spans kilometers and inserts foreign
        // vertices into edges they never belonged to (zigzag artifacts).
        const tolerance = this.topologyEpsilon();
        const toleranceSquared = tolerance * tolerance;
        const coincidentSquared = 1e-12;
        let changed = false;

        const committedVertices = (rings: number[][][]): number[][] => {
            const out: number[][] = [];
            for (const ring of rings) {
                for (let vertexIndex = 0; vertexIndex < ring.length - 1; vertexIndex++) {
                    out.push(ring[vertexIndex]);
                }
            }
            return out;
        };

        const containsCoincident = (vertices: number[][], point: number[]): boolean =>
            vertices.some(vertex => this.squaredEuclideanDistance(vertex, point) <= coincidentSquared);

        for (const targetFeature of features) {
            const oldRings = this.preModifySnapshot.get(targetFeature);
            const geometry = targetFeature.getGeometry();
            const currentRings = this.getGeometryRings(geometry);
            if (!oldRings || !geometry || !currentRings) continue;

            const oldVertices = committedVertices(oldRings);
            const currentVertices = committedVertices(currentRings);

            // Positions that appeared during the edit (typically the single
            // moved-to position of the dragged vertex).
            const movedTo = currentVertices.filter(currentVertex => !containsCoincident(oldVertices, currentVertex));

            // Collect stationary foreign vertices per OLD edge of the target.
            type PendingEdge = { ringIndex: number; a: number[]; b: number[]; candidates: number[][] };
            const pendingByEdge = new Map<string, PendingEdge>();

            for (const sourceFeature of features) {
                if (sourceFeature === targetFeature) continue;
                const sourceRings = this.getGeometryRings(sourceFeature.getGeometry());
                const sourceOldRings = this.preModifySnapshot.get(sourceFeature);
                if (!sourceRings || !sourceOldRings) continue;

                const sourceOldVertices = committedVertices(sourceOldRings);

                for (const sourceRing of sourceRings) {
                    for (let vertexIndex = 0; vertexIndex < sourceRing.length - 1; vertexIndex++) {
                        const candidate = sourceRing[vertexIndex];

                        // Only stationary vertices — the dragged vertex itself
                        // is handled natively by OL (segment insertion).
                        if (!containsCoincident(sourceOldVertices, candidate)) continue;
                        // Already shared with the target — nothing to repair.
                        if (containsCoincident(currentVertices, candidate)) continue;

                        // Did the candidate lie on an OLD edge of the target?
                        for (let ringIndex = 0; ringIndex < oldRings.length; ringIndex++) {
                            const oldRing = oldRings[ringIndex];
                            let matched = false;
                            for (let segmentIndex = 0; segmentIndex < oldRing.length - 1; segmentIndex++) {
                                const segmentStart = oldRing[segmentIndex];
                                const segmentEnd = oldRing[segmentIndex + 1];
                                if (this.squaredEuclideanDistance(candidate, segmentStart) <= coincidentSquared) continue;
                                if (this.squaredEuclideanDistance(candidate, segmentEnd) <= coincidentSquared) continue;

                                const {distanceSquared, parameterT} = this.pointSegmentsDistance(candidate, segmentStart, segmentEnd);
                                if (distanceSquared <= toleranceSquared && parameterT > 0 && parameterT < 1) {
                                    const edgeMapKey = `${ringIndex}:${segmentIndex}`;
                                    let pending = pendingByEdge.get(edgeMapKey);
                                    if (!pending) {
                                        pending = {ringIndex, a: segmentStart, b: segmentEnd, candidates: []};
                                        pendingByEdge.set(edgeMapKey, pending);
                                    }
                                    if (!containsCoincident(pending.candidates, candidate)) {
                                        pending.candidates.push([candidate[0], candidate[1]]);
                                    }
                                    matched = true;
                                    break;
                                }
                            }
                            if (matched) break;
                        }
                    }
                }
            }

            if (!pendingByEdge.size) continue;

            let featureChanged = false;

            pendingByEdge.forEach(pending => {
                const ring = currentRings[pending.ringIndex];
                if (!ring) return;

                // Resolve the current position of each old endpoint: either it
                // is still present (stationary) or it moved — resolvable only
                // when exactly one new position appeared during the gesture.
                const resolveEndpoint = (oldPoint: number[]): { point: number[]; moved: boolean } | null => {
                    for (let vertexIndex = 0; vertexIndex < ring.length - 1; vertexIndex++) {
                        if (this.squaredEuclideanDistance(ring[vertexIndex], oldPoint) <= coincidentSquared) {
                            return {point: ring[vertexIndex], moved: false};
                        }
                    }
                    return movedTo.length === 1 ? {point: movedTo[0], moved: true} : null;
                };

                const endpointA = resolveEndpoint(pending.a);
                const endpointB = resolveEndpoint(pending.b);
                if (!endpointA || !endpointB) return;

                // The old edge is fully intact — no discontinuity was created
                // here; the start-time/proximity harmonization covers it.
                if (!endpointA.moved && !endpointB.moved) return;

                // Locate the current segment between the resolved endpoints.
                const lastIndex = ring.length - 1;
                let insertAt = -1;
                let segmentStart: number[] | null = null;
                for (let vertexIndex = 0; vertexIndex < lastIndex; vertexIndex++) {
                    const p = ring[vertexIndex];
                    const q = ring[vertexIndex + 1];
                    const matchesForward = this.squaredEuclideanDistance(p, endpointA.point) <= coincidentSquared &&
                        this.squaredEuclideanDistance(q, endpointB.point) <= coincidentSquared;
                    const matchesReverse = this.squaredEuclideanDistance(p, endpointB.point) <= coincidentSquared &&
                        this.squaredEuclideanDistance(q, endpointA.point) <= coincidentSquared;
                    if (matchesForward || matchesReverse) {
                        insertAt = vertexIndex + 1;
                        segmentStart = p;
                        break;
                    }
                }
                if (insertAt < 0 || !segmentStart) return;

                // Insert all candidates ordered along the current segment.
                const segmentEnd = ring[insertAt];
                const ordered = pending.candidates
                    .map(candidate => ({candidate, parameterT: this.pointSegmentsDistance(candidate, segmentStart!, segmentEnd).parameterT}))
                    .sort((entryA, entryB) => entryA.parameterT - entryB.parameterT)
                    .map(entry => entry.candidate);

                ring.splice(insertAt, 0, ...ordered);
                featureChanged = true;
            });

            if (featureChanged) {
                this.setGeometryRings(geometry, currentRings);
                changed = true;
            }
        }

        return changed;
    }

    private rebuildEdgeIndex(sources: Array<{ source: VectorSource<Feature>, layer: string }>) {
        this.edgeIndex.clear();
        this.featureLayerMap = new WeakMap<Feature, string>();

        for (const {source, layer} of sources) {
            source.getFeatures().forEach((feature) => {
                const geometry = feature.getGeometry();
                const rings = this.getGeometryRings(geometry);
                if (!geometry || !rings) return;

                this.featureLayerMap.set(feature, layer);
                for (let ringIndex = 0; ringIndex < rings.length; ringIndex++) {
                    const ring = rings[ringIndex];
                    for (let vertexIndex = 0; vertexIndex < ring.length - 1; vertexIndex++) {
                        const edgeKeyStr = this.edgeKey(ring[vertexIndex] as [number, number], ring[vertexIndex + 1] as [number, number]);
                        let edgeEntries = this.edgeIndex.get(edgeKeyStr);
                        if (!edgeEntries) this.edgeIndex.set(edgeKeyStr, (edgeEntries = []));
                        edgeEntries.push({feature, ring: ringIndex, i: vertexIndex});
                    }
                }
            });
        }
    }

    /**
     * Detect a multi-touch (two-finger pinch-zoom) gesture on mobile. Such a
     * gesture reports two or more simultaneously active pointers on the map
     * browser event, whereas a single finger, mouse or pen reports one. Vertex
     * selection and dragging are suppressed while this is true so pinch-zoom
     * never accidentally grabs or moves a vertex — behavior that is identical
     * with and without the topology guard, since both share this condition.
     */
    private isMultiTouchGesture(event: MapBrowserEvent<any>): boolean {
        const activePointers = (event as any).activePointers as PointerEvent[] | undefined;
        return Array.isArray(activePointers) && activePointers.length >= 2;
    }

    /**
     * Wrap a Modify interaction's drag handler so it ignores drag events fired
     * while a multi-touch pinch-zoom gesture is in progress.
     *
     * OpenLayers dispatches pointer-drag events during a two-finger gesture
     * whose coordinate is the CENTROID of both fingers. If the first finger
     * happened to land on (and grab) a vertex, Modify would then drag that
     * vertex along with the centroid, silently deforming the geometry while
     * the user only meant to zoom. The {@link isMultiTouchGesture} check in
     * the interaction's `condition` blocks the initial grab when both fingers
     * are already down, but a vertex grabbed by the first finger before the
     * second touches still needs its drag suppressed — this guard does that.
     * Once a finger lifts (active pointers drop below two) drag events flow to
     * the original handler again, re-enabling normal vertex editing.
     */
    private guardModifyAgainstMultiTouch(modify: Modify): void {
        const originalHandleDragEvent = (modify as any).handleDragEvent.bind(modify);
        (modify as any).handleDragEvent = (event: MapBrowserEvent<any>) => {
            if (this.isMultiTouchGesture(event)) return;
            originalHandleDragEvent(event);
        };
    }

    private createModify(
        target: { source?: VectorSource<Feature>; features?: Collection<Feature> },
        options?: ModifyOptions,
        callback?: GetIDs
    ) {
        const fallbackModify: SymbolDefinition = {
            "image": {
                "type": "circle",
                "radius": 6,
                "fill": {
                    "color": "rgba(25,118,210,0.9)"
                },
                "stroke": {
                    "color": "rgb(25,118,210)",
                    "width": 2
                }
            }
        };

        const fallbackVertex: SymbolDefinition = {
            "image": {
                "type": "circle",
                "radius": 6,
                "fill": {
                    "color": "rgba(255,255,255,0.9)"
                },
                "stroke": {
                    "color": "rgb(25,118,210)",
                    "width": 2
                }
            }
        };

        const modifyConfig =
            this.editConfiguration?.modify?.modifyStyle ?? fallbackModify;

        const vertexConfig =
            this.editConfiguration?.modify?.vertexStyle ?? fallbackVertex;

        const modifyStyle = createOlStyle(modifyConfig);
        const vertexStyle = createOlStyle(vertexConfig);

        const styleFunction: StyleFunction = (vertexFeature: FeatureLike, _resolution: number) => {
            const isExisting = !!(vertexFeature as any).get?.('existing');
            return isExisting ? vertexStyle : modifyStyle;
        };

        const modeOption = options?.mode;
        const modes: ModifyMode[] = Array.isArray(modeOption)
            ? modeOption
            : (modeOption ? [modeOption] : ['default']);
        const allowSingleClickDelete = modes.includes('deleteSingleClick');
        const allowRightClickDelete = modes.includes('deleteRightClick');

        const conditionFunction = (event: MapBrowserEvent<any>): boolean => {
            // Never grab a vertex while a two-finger pinch-zoom gesture is in
            // progress on mobile (see #223584/#223585): the second finger
            // would otherwise start dragging whatever vertex the first finger
            // landed on.
            if (this.isMultiTouchGesture(event)) return false;
            // In a topology-guarded session a dragged-along neighbor may only
            // be edited where it overlaps the edited feature: block a grab
            // that is not on/near a PRIMARY feature's boundary, so a shared
            // vertex or shared edge stays grabbable but the neighbor's own
            // non-shared vertices do not. Uses OpenLayers' default 10 px grab
            // tolerance so the gate matches what the interaction would snap to.
            if (this.restrictNeighborEditsToSharedBoundary && this.primaryEditFeatures.length) {
                const tolerance = this.mapToleranceFromPixels(10);
                if (!this.coordinateOnPrimaryBoundary(event.coordinate, tolerance * tolerance)) {
                    return false;
                }
            }
            if (primaryAction(event)) return true;
            if (allowRightClickDelete) {
                const originalEvent = event.originalEvent as PointerEvent | MouseEvent | undefined;
                if (originalEvent?.button === 2) return true;
            }
            return false;
        };

        const deleteConditionFunction = (event: MapBrowserEvent<any>): boolean => {
            // OL default: Shift + single click.
            if (singleClick(event) && shiftKeyOnly(event)) return true;
            // Opt-in: plain single click (no modifier).
            if (allowSingleClickDelete && singleClick(event)) return true;
            // Opt-in: right-click. We accept it on pointerup since OL doesn't
            // emit a `contextmenu` MapBrowserEvent.
            if (allowRightClickDelete) {
                const originalEvent = event.originalEvent as PointerEvent | MouseEvent | undefined;
                if (event.type === 'pointerup' && originalEvent?.button === 2) {
                    return true;
                }
            }
            return false;
        };

        const topologyGuardEnabled = (options as any)?.topologyGuard === true;
        let modifyFeatures: Collection<Feature>;

        if (topologyGuardEnabled && this.topologySources.length > 0) {
            modifyFeatures = new Collection<Feature>();

            const primaryFeatureSet = new Set<Feature>();

            if (target.features) {
                target.features.forEach(feature => {
                    modifyFeatures.push(feature);
                    primaryFeatureSet.add(feature);
                    this.featureLayerMap.set(feature, this.currentEditedLayerName!);
                });
            } else if (target.source) {
                target.source.getFeatures().forEach(feature => {
                    modifyFeatures.push(feature);
                    primaryFeatureSet.add(feature);
                    this.featureLayerMap.set(feature, this.currentEditedLayerName!);
                });
            }

            // Track the directly-edited features so the neighbor grab
            // restriction below can tell them apart from dragged-along
            // neighbors added afterwards.
            this.primaryEditFeatures = Array.from(primaryFeatureSet);
            // Restrict dragged-along neighbors to their shared boundary: only
            // grabs on/near a primary feature's boundary are allowed.
            this.restrictNeighborEditsToSharedBoundary = true;

            // Collect all edges and vertices of the primary features
            const primaryEdgeKeys = new Set<string>();
            const primaryVertices: number[][] = [];
            const primarySegments: { a: number[]; b: number[] }[] = [];
            primaryFeatureSet.forEach(primaryFeature => {
                const rings = this.getGeometryRings(primaryFeature.getGeometry());
                if (!rings) return;
                for (let ringIndex = 0; ringIndex < rings.length; ringIndex++) {
                    const ring = rings[ringIndex];
                    for (let vertexIndex = 0; vertexIndex < ring.length - 1; vertexIndex++) {
                        primaryEdgeKeys.add(this.edgeKey(ring[vertexIndex] as [number, number], ring[vertexIndex + 1] as [number, number]));
                        primaryVertices.push(ring[vertexIndex]);
                        primarySegments.push({a: ring[vertexIndex], b: ring[vertexIndex + 1]});
                    }
                }
            });

            // Tolerance for detecting touch relations that are not exact
            // edge matches: shared single vertices and T-nodes (a vertex of
            // one polygon lying on the interior of the other's edge).
            // Data-space epsilon — zoom must not affect topology decisions.
            const touchTolerance = this.topologyEpsilon();
            // For neighbor INCLUSION, be as forgiving as the weld pass:
            // corners stored a few cm apart are still the same touch point.
            const inclusionTolerance = Math.max(touchTolerance, this.weldToleranceValue());
            const inclusionToleranceSquared = inclusionTolerance * inclusionTolerance;

            // Only add neighbor features that actually touch the primary
            // features: shared edge, shared vertex, or T-node contact.
            const neighborNames = (options as any)?.neighbors as string[] | undefined;
            if (neighborNames?.length) {
                for (const neighborName of neighborNames) {
                    try {
                        const neighborLayer = this.GIS.Layer.getLayer(neighborName);
                        const neighborSource = neighborLayer.getSource() as VectorSource<Feature>;

                        neighborSource.getFeatures().forEach(neighborFeature => {
                            if (primaryFeatureSet.has(neighborFeature)) return; // already included
                            if (modifyFeatures.getArray().includes(neighborFeature)) return;

                            const neighborRings = this.getGeometryRings(neighborFeature.getGeometry());
                            if (!neighborRings) return;

                            let touchesPrimary = false;

                            outer: for (let ringIndex = 0; ringIndex < neighborRings.length; ringIndex++) {
                                const ring = neighborRings[ringIndex];
                                for (let vertexIndex = 0; vertexIndex < ring.length - 1; vertexIndex++) {
                                    const neighborVertex = ring[vertexIndex];
                                    const neighborNext = ring[vertexIndex + 1];

                                    // (a) Exact shared edge.
                                    const edgeKeyStr = this.edgeKey(neighborVertex as [number, number], neighborNext as [number, number]);
                                    if (primaryEdgeKeys.has(edgeKeyStr)) {
                                        touchesPrimary = true;
                                        break outer;
                                    }

                                    // (b) Neighbor vertex coincides with a primary
                                    // vertex, or lies on a primary edge (T-node).
                                    for (const segment of primarySegments) {
                                        const {distanceSquared} = this.pointSegmentsDistance(neighborVertex, segment.a, segment.b);
                                        if (distanceSquared <= inclusionToleranceSquared) {
                                            touchesPrimary = true;
                                            break outer;
                                        }
                                    }

                                    // (c) Primary vertex lies on this neighbor
                                    // edge (reverse T-node).
                                    for (const primaryVertex of primaryVertices) {
                                        const {distanceSquared} = this.pointSegmentsDistance(primaryVertex, neighborVertex, neighborNext);
                                        if (distanceSquared <= inclusionToleranceSquared) {
                                            touchesPrimary = true;
                                            break outer;
                                        }
                                    }
                                }
                            }

                            if (touchesPrimary) {
                                modifyFeatures.push(neighborFeature);

                                this.featureLayerMap.set(neighborFeature, neighborName);
                            }
                        });
                    } catch (error) {
                        console.warn(`Could not add neighbor layer ${neighborName}:`, error);
                    }
                }
            }

            // Remember the whole guarded set (primary + touching neighbors) so
            // modifyend can harmonize over all of them and report every
            // neighbor the guard adjusts, not only the features OpenLayers
            // dragged directly.
            this.guardedFeatures = modifyFeatures.getArray().slice();

            // Harmonize topology before editing starts. Three passes:
            // - weld: rewrite nearly-coincident corners of different
            //   features (cm-level data noise) to EXACT shared coordinates —
            //   all subsequent corner-identity checks depend on this,
            // - proximity: vertices within the data-space epsilon of an edge,
            // - chains: vertices between exactly-shared segment endpoints
            //   (robust against data imprecision — relative tolerance).
            const weldedCorners = this.weldNearCoincidentCorners(modifyFeatures.getArray());
            const harmonizedProximity = this.harmonizeTopologyVertices(modifyFeatures.getArray(), touchTolerance);
            const harmonizedChains = this.harmonizeSharedBoundaryChains(modifyFeatures.getArray());
            if (weldedCorners || harmonizedProximity || harmonizedChains) {
                this.rebuildEdgeIndex(this.topologySources);
            }

            this.modifyTargetCollection = modifyFeatures;
            this.olModifyInteraction = new Modify({
                features: modifyFeatures,
                condition: conditionFunction,
                deleteCondition: deleteConditionFunction,
                style: styleFunction,
            });
        } else {
            // No topology guard — every modified feature is a direct edit
            // target and there is no neighbor grab restriction.
            this.primaryEditFeatures = target.features
                ? target.features.getArray().slice()
                : (target.source?.getFeatures().slice() ?? []);
            this.guardedFeatures = [];
            this.restrictNeighborEditsToSharedBoundary = false;

            // Source-backed interactions track their source themselves, so only
            // a Collection target needs pruning when a feature disappears.
            this.modifyTargetCollection = target.features;
            this.olModifyInteraction = new Modify({
                ...(target.features ? {features: target.features} : {source: target.source!}),
                condition: conditionFunction,
                deleteCondition: deleteConditionFunction,
                style: styleFunction,
            });
        }

        // Suppress vertex dragging during multi-touch pinch-zoom (mobile).
        this.guardModifyAgainstMultiTouch(this.olModifyInteraction);

        this.olModifyInteraction.on('modifystart', (modifyEvent) => {
            this.preModifySnapshot.clear();
            this.touchedIdsByLayer.clear();
            this.activeVertexInfo = new WeakMap();
            // Drop any live snap targets from a previous gesture before wiring
            // up the vertex grabbed this time (see armAdjacentVertexSnap).
            this.adjacentSnapFeatures.clear();

            modifyEvent.features.forEach((feature: Feature) => this.capturePreModifySnapshot(feature));

            // Topology guard: also baseline every neighbor in the guarded set,
            // even ones OpenLayers will not drag directly. The guard may still
            // adjust their shared borders at modifyend; comparing against this
            // gesture-start baseline is what lets those neighbors be reported.
            this.guardedFeatures.forEach((feature: Feature) => this.capturePreModifySnapshot(feature));

            // Exclude the feature(s) under the pointer from their own snap
            // candidates so a vertex cannot snap to the SAME polygon boundary.
            this.excludeEditedFeaturesFromSelfSnap(modifyEvent.features.getArray() as Feature[]);

            // Arm live self-intersection feedback for the whole gesture (the
            // edited feature plus any topology-connected neighbors moving with
            // it). The pre-gesture geometry is already captured above in
            // preModifySnapshot, which is what a rejected gesture reverts to.
            if (this.preventSelfIntersection) {
                this.clearSelfIntersectionListeners();
                this.gestureFeatures = modifyEvent.features.getArray() as Feature[];
                this.gestureFeatures.forEach((feature: Feature) => {
                    const geometry = feature.getGeometry();
                    if (!geometry) return;
                    const key = geometry.on('change', () => this.refreshSelfIntersectionFeedback());
                    this.selfIntersectionListenerKeys.push(key);
                });
            }

            const start = (modifyEvent as any).mapBrowserEvent?.coordinate as number[] | undefined;
            if (!start) return;

            const tolerance = this.mapToleranceFromPixels(8);
            const toleranceSquared = tolerance * tolerance;

            modifyEvent.features.forEach((feature: Feature) => {
                const oldRings = this.preModifySnapshot.get(feature);
                if (!oldRings) return;

                let foundExisting: { ring: number; i: number } | null = null;
                for (let ringIndex = 0; ringIndex < oldRings.length && !foundExisting; ringIndex++) {
                    const ring = oldRings[ringIndex];
                    const lastVertexIndex = ring.length - 1;
                    for (let vertexIndex = 0; vertexIndex < lastVertexIndex; vertexIndex++) {
                        if (this.squaredEuclideanDistance(ring[vertexIndex], start) <= toleranceSquared) {
                            foundExisting = {ring: ringIndex, i: vertexIndex};
                            break;
                        }
                    }
                }

                if (foundExisting) {
                    this.activeVertexInfo.set(feature, {
                        type: 'existing',
                        ring: foundExisting.ring,
                        vertexIndex: foundExisting.i,
                    });
                    return;
                }

                let bestMatch: { ring: number; i: number; distanceSquared: number } | null = null;
                for (let ringIndex = 0; ringIndex < oldRings.length; ringIndex++) {
                    const ring = oldRings[ringIndex];
                    const lastVertexIndex = ring.length - 1;
                    for (let vertexIndex = 0; vertexIndex < lastVertexIndex; vertexIndex++) {
                        const segmentStart = ring[vertexIndex], segmentEnd = ring[(vertexIndex + 1) % lastVertexIndex];
                        const {distanceSquared} = this.pointSegmentsDistance(start, segmentStart, segmentEnd);
                        if (bestMatch === null || distanceSquared < bestMatch.distanceSquared) bestMatch = {ring: ringIndex, i: vertexIndex, distanceSquared};
                    }
                }

                if (bestMatch) {
                    const ring = oldRings[bestMatch.ring];
                    const lastVertexIndex = ring.length - 1;
                    const segmentStart = ring[bestMatch.i];
                    const segmentEnd = ring[(bestMatch.i + 1) % lastVertexIndex];
                    this.activeVertexInfo.set(feature, {
                        type: 'inserted',
                        ring: bestMatch.ring,
                        oldEdge: {a: segmentStart, b: segmentEnd},
                    });
                }
            });

            // Feed the grabbed vertex's two neighbours to the live cursor snap
            // so it magnetically merges onto an adjacent corner while dragging.
            // Gated like the on-release cleanup: only when the coincident-merge
            // pass will run to collapse the duplicate the snap creates.
            if (this.preventSelfIntersection) {
                this.armAdjacentVertexSnap(start);
            }
        });

        this.olModifyInteraction.on('modifyend', (event) => {
            const modifiedFeatures = event.features.getArray() as Feature[];

            // The gesture is over — retire this drag's live cursor snap targets
            // (both the accepted and the early-return rejected path pass here).
            this.adjacentSnapFeatures.clear();

            // Reject a drag that ended on a self-intersecting geometry: revert
            // every feature that moved back to its per-gesture origin.
            if (this.preventSelfIntersection) {
                this.clearSelfIntersectionListeners();
                this.setInvalidFeedback(false);

                const rejected = this.enforceNoSelfIntersection(modifiedFeatures);

                // Re-add the dragged feature(s) to their own snap candidates
                // only AFTER the geometry has been restored, so the snap index
                // never captures the transient invalid shape.
                this.restoreSelfSnapFeatures();

                if (rejected) {
                    this.gestureFeatures = [];
                    // The gesture is fully undone — the geometry is back at its
                    // origin. Treat it as a NO-OP: skip linked-layer
                    // propagation, topology harmonization and the change
                    // callback. Running them here would mutate the restored
                    // geometry (weld/insert neighbor vertices from adjacent
                    // features), corrupting the origin so the NEXT drag starts
                    // from a shifted, neighbor-influenced state instead of the
                    // true origin. Only keep the edge index and the on-screen
                    // measurement in step with the restored geometry.
                    if (topologyGuardEnabled && this.topologySources.length) {
                        this.rebuildEdgeIndex(this.topologySources);
                    }
                    modifiedFeatures.forEach((feature) => this.updateMeasurement(feature, false));
                    this.GIS.Overlay.hide();

                    // Tell the caller WHY the vertex jumped back. Without this
                    // the revert is indistinguishable from "the vertex snapped
                    // somewhere else": the change callback is deliberately not
                    // fired (nothing changed), so an app that only listens there
                    // sees a silent snap-back with no explanation.
                    this.reportRejectedGesture(options, modifiedFeatures);
                    return;
                }

                this.gestureFeatures = [];

                // Accepted drag: a vertex that could not snap to its own
                // boundary but was dropped close to one of its immediate
                // neighbors (left/right) snaps exactly onto it, then any vertex
                // that landed exactly on its neighbor is collapsed into a single
                // corner — so no duplicate vertex remains on the polygon.
                this.snapDraggedVertexToAdjacentVertex(modifiedFeatures, this.adjacentVertexSnapTolerance());
                this.mergeAdjacentCoincidentVertices(modifiedFeatures);
            } else {
                // No self-intersection guard — restore snap candidates as the
                // gesture ends.
                this.restoreSelfSnapFeatures();
            }

            // Propagate vertex moves to linked layers
            const linkedLayers = options?.linkedLayers;
            if (linkedLayers?.length) {
                this.propagateToLinkedLayers(modifiedFeatures, linkedLayers);
            }

            const primaryFeatures: Feature[] = [];

            if (topologyGuardEnabled && this.currentEditedLayerName) {
                modifiedFeatures.forEach(feature => {
                    const featureLayer = this.featureLayerMap.get(feature);
                    if (featureLayer === this.currentEditedLayerName) {
                        primaryFeatures.push(feature);
                    }
                });
            } else {
                primaryFeatures.push(...modifiedFeatures);
            }

            const flatIDs = primaryFeatures
                .map((feature) => this.featureIdValue(feature, this.resolveFeatureLayer(feature)))
                .filter((id): id is string | number => id !== undefined);

            if (topologyGuardEnabled) {
                // The guard may adjust neighbors OpenLayers never dragged
                // directly (shared borders re-aligned through the harmonization
                // passes below), so operate over the whole guarded set —
                // primary features plus every touching neighbor — not just the
                // features reported in `event.features`.
                const guardedSet = this.collectGuardedFeatures(modifiedFeatures);

                // Always report the directly-modified features.
                modifiedFeatures.forEach(feature => this.recordTouchedFeature(feature));

                // Repair discontinuities first: a stationary T-node vertex
                // whose supporting edge endpoint was just moved away (the
                // neighbor edge now cuts across it). Must run before the
                // proximity pass, which cannot see these cases anymore.
                this.repairTNodeDiscontinuities(guardedSet);

                // A vertex may have just been dragged onto (or next to) a
                // neighbor vertex or edge. Weld near-coincident corners
                // first, then harmonize again so new T-nodes become true
                // shared vertices before the next drag. The chain pass
                // re-aligns shared boundaries detected via exact endpoints.
                this.weldNearCoincidentCorners(guardedSet);
                this.harmonizeTopologyVertices(guardedSet);
                this.harmonizeSharedBoundaryChains(guardedSet);

                // Report every neighbor whose vertices or borders the guard
                // actually changed (relative to the gesture-start snapshot),
                // alongside the directly-edited polygon(s).
                guardedSet.forEach(feature => {
                    if (this.featureGeometryChangedSincePreModify(feature)) {
                        this.recordTouchedFeature(feature);
                    }
                });

                if (this.topologySources.length) {
                    this.rebuildEdgeIndex(this.topologySources);
                }

                const idsByLayer: Record<string, (string | number)[]> = {};
                this.touchedIdsByLayer.forEach((ids, layerName) => {
                    idsByLayer[layerName] = Array.from(ids);
                });

                callback?.(idsByLayer as any);
            } else {
                callback?.(flatIDs);
            }

            modifiedFeatures.forEach((feature) => this.updateMeasurement(feature, false));
            this.GIS.Overlay.hide();
        });
    }

    private attachContextMenuSuppress() {
        this.detachContextMenuSuppress();
        const map = this.GIS.Map.olObject;
        if (!map) return;

        // Suppress the native browser context menu so the right-click
        // delete inside OL's deleteCondition isn't masked by the OS menu.
        const viewport = map.getViewport();
        this.suppressContextMenuHandler = (e: Event) => e.preventDefault();
        viewport.addEventListener('contextmenu', this.suppressContextMenuHandler);
    }

    private detachContextMenuSuppress() {
        if (this.suppressContextMenuHandler) {
            const viewport = this.GIS.Map.olObject?.getViewport();
            viewport?.removeEventListener('contextmenu', this.suppressContextMenuHandler);
            this.suppressContextMenuHandler = undefined;
        }
    }

    private attachDeleteVertexOnHover(callback?: GetIDs, topologyGuardEnabled: boolean = false) {
        this.detachDeleteVertexOnHover();
        const map = this.GIS.Map.olObject;
        if (!map) return;

        const viewport = map.getViewport();
        if (!viewport.hasAttribute('tabindex')) {
            viewport.setAttribute('tabindex', '0');
        }

        this.deleteVertexOnHoverHandler = (event: KeyboardEvent) => {
            if (event.key !== 'Delete' && event.key !== 'Backspace') return;

            const modify = this.olModifyInteraction as any;
            if (!modify) return;

            const snappedToVertex: boolean = modify.snappedToVertex_ === true;
            const vertexFeature = modify.vertexFeature_ as Feature | undefined;
            if (!snappedToVertex || !vertexFeature) return;

            const vertexGeom = vertexFeature.getGeometry() as Point | undefined;
            if (!vertexGeom) return;
            const [hoveredX, hoveredY] = vertexGeom.getCoordinates();

            const removed = this.removeHoveredVertexFromFeatures(hoveredX, hoveredY);
            if (!removed) return;

            event.preventDefault();
            event.stopPropagation();

            // Clear the highlight - the vertex is gone.
            const overlaySource = modify.overlay_?.getSource?.();
            if (overlaySource && typeof overlaySource.removeFeature === 'function') {
                try { overlaySource.removeFeature(vertexFeature); } catch { /* ignore */ }
            }
            modify.vertexFeature_ = null;
            modify.snappedToVertex_ = false;

            if (topologyGuardEnabled && this.topologySources.length) {
                this.rebuildEdgeIndex(this.topologySources);
            }

            if (callback) {
                if (topologyGuardEnabled) {
                    const idsByLayer: Record<string, (string | number)[]> = {};
                    removed.byLayer.forEach((ids, layerName) => {
                        idsByLayer[layerName] = Array.from(ids);
                    });
                    callback(idsByLayer as any);
                } else {
                    callback(removed.featureIds);
                }
            }
        };

        viewport.addEventListener('keydown', this.deleteVertexOnHoverHandler);
        document.addEventListener('keydown', this.deleteVertexOnHoverHandler);
    }

    private detachDeleteVertexOnHover() {
        if (this.deleteVertexOnHoverHandler) {
            const viewport = this.GIS.Map.olObject?.getViewport();
            viewport?.removeEventListener('keydown', this.deleteVertexOnHoverHandler);
            document.removeEventListener('keydown', this.deleteVertexOnHoverHandler);
            this.deleteVertexOnHoverHandler = undefined;
        }
    }

    private removeHoveredVertexFromFeatures(
        targetX: number,
        targetY: number
    ): { featureIds: (string | number)[]; byLayer: Map<string, Set<string | number>> } | null {
        const modify = this.olModifyInteraction as any;
        if (!modify) return null;

        const features: Feature[] = [];
        if (modify.features_ && typeof modify.features_.forEach === 'function') {
            modify.features_.forEach((feature: Feature) => features.push(feature));
        } else if (modify.source_ && typeof modify.source_.getFeatures === 'function') {
            features.push(...modify.source_.getFeatures());
        }

        const pixelTolerance = this.mapToleranceFromPixels(2);
        const toleranceSquared = pixelTolerance * pixelTolerance;

        const affectedFeatures: Feature[] = [];

        for (const feature of features) {
            const geometry = feature.getGeometry();
            if (!geometry) continue;

            if (geometry.getType() === 'Polygon' || geometry.getType() === 'MultiPolygon') {
                const liveRings = this.getGeometryRings(geometry)!;
                const rings = liveRings.map(ring => ring.map(coord => [coord[0], coord[1]]));
                let mutated = false;

                for (let ringIndex = 0; ringIndex < rings.length && !mutated; ringIndex++) {
                    const ring = rings[ringIndex];
                    // Polygon rings are closed (last == first); iterate unique vertices.
                    for (let vertexIndex = 0; vertexIndex < ring.length - 1; vertexIndex++) {
                        const deltaX = ring[vertexIndex][0] - targetX;
                        const deltaY = ring[vertexIndex][1] - targetY;
                        if (deltaX * deltaX + deltaY * deltaY <= toleranceSquared) {
                            // Need at least 3 unique vertices to remain a valid ring.
                            if (ring.length - 1 <= 3) {
                                return null;
                            }
                            ring.splice(vertexIndex, 1);
                            if (vertexIndex === 0) {
                                ring[ring.length - 1] = [ring[0][0], ring[0][1]];
                            }
                            mutated = true;
                            break;
                        }
                    }
                }

                if (mutated) {
                    this.setGeometryRings(geometry, rings);
                    affectedFeatures.push(feature);
                }
            } else if (geometry.getType() === 'LineString') {
                const line = geometry as LineString;
                const coordinates = line.getCoordinates().map(coord => [coord[0], coord[1]]);
                for (let vertexIndex = 0; vertexIndex < coordinates.length; vertexIndex++) {
                    const deltaX = coordinates[vertexIndex][0] - targetX;
                    const deltaY = coordinates[vertexIndex][1] - targetY;
                    if (deltaX * deltaX + deltaY * deltaY <= toleranceSquared) {
                        if (coordinates.length <= 2) return null;
                        coordinates.splice(vertexIndex, 1);
                        line.setCoordinates(coordinates);
                        affectedFeatures.push(feature);
                        break;
                    }
                }
            }
        }

        if (!affectedFeatures.length) return null;

        const featureIds: (string | number)[] = [];
        const byLayer = new Map<string, Set<string | number>>();
        for (const feature of affectedFeatures) {
            const layer = this.resolveFeatureLayer(feature);
            const id = this.featureIdValue(feature, layer);
            if (id !== undefined) {
                featureIds.push(id);
                if (!byLayer.has(layer)) byLayer.set(layer, new Set());
                byLayer.get(layer)!.add(id);
                if (!this.touchedIdsByLayer.has(layer)) this.touchedIdsByLayer.set(layer, new Set());
                this.touchedIdsByLayer.get(layer)!.add(id);
            }
            this.updateMeasurement(feature, false);
        }
        this.GIS.Overlay.hide();

        return { featureIds, byLayer };
    }


    private propagateToLinkedLayers(modifiedFeatures: Feature[], linkedLayerNames: string[]) {
        // Use a fixed tolerance in map units (not zoom-dependent) for reliable vertex matching.
        // 0.01 map units ≈ 1cm in metric projections, sufficient for coordinate snapping.
        const tolerance = 0.01;
        const toleranceSquared = tolerance * tolerance;

        for (const feature of modifiedFeatures) {
            const oldSnapshot = this.preModifySnapshot.get(feature);
            if (!oldSnapshot) continue;

            const geometry = feature.getGeometry();
            if (!geometry) continue;

            const movedVertices: Array<{ from: number[]; to: number[] }> = [];
            const insertedVertices: Array<{ edgeStart: number[]; edgeEnd: number[]; vertex: number[] }> = [];
            const removedVertices: number[][] = [];

            if (geometry.getType() === 'Polygon') {
                const newRings = (geometry as Polygon).getCoordinates();
                for (let ringIndex = 0; ringIndex < oldSnapshot.length && ringIndex < newRings.length; ringIndex++) {
                    const oldRing = oldSnapshot[ringIndex];
                    const newRing = newRings[ringIndex];
                    this.diffRing(oldRing, newRing, toleranceSquared, movedVertices, insertedVertices, removedVertices);
                }
            } else if (geometry.getType() === 'LineString') {
                const oldCoords = oldSnapshot[0];
                const newCoords = (geometry as LineString).getCoordinates();
                if (oldCoords) {
                    this.diffRing(oldCoords, newCoords, toleranceSquared, movedVertices, insertedVertices, removedVertices);
                }
            } else if (geometry.getType() === 'Point') {
                const oldCoord = oldSnapshot[0]?.[0];
                const newCoord = (geometry as Point).getCoordinates();
                if (oldCoord) {
                    const deltaX = oldCoord[0] - newCoord[0];
                    const deltaY = oldCoord[1] - newCoord[1];
                    if (deltaX * deltaX + deltaY * deltaY > toleranceSquared) {
                        movedVertices.push({ from: oldCoord, to: newCoord });
                    }
                }
            }

            if (movedVertices.length === 0 && insertedVertices.length === 0 && removedVertices.length === 0) continue;

            for (const layerName of linkedLayerNames) {
                try {
                    const linkedSource = this.GIS.Layer.getLayer(layerName).getSource() as VectorSource<Feature>;
                    for (const linkedFeature of linkedSource.getFeatures()) {
                        if (linkedFeature === feature) continue;
                        if (movedVertices.length > 0) {
                            this.applyVertexMoves(linkedFeature, movedVertices, toleranceSquared);
                        }
                        if (insertedVertices.length > 0) {
                            this.applyVertexInsertions(linkedFeature, insertedVertices, toleranceSquared);
                        }
                        if (removedVertices.length > 0) {
                            this.applyVertexRemovals(linkedFeature, removedVertices, toleranceSquared);
                        }
                    }
                } catch { /* layer not found */ }
            }
        }
    }

    /**
     * Diff old vs new coordinate arrays: same-length is a vertex move, longer is
     * an insertion, shorter is a DELETION (e.g. the right-click / single-click
     * delete modes, or Shift+click). All three must propagate to linked layers —
     * a deletion that does not leaves the linked geometry with a vertex the
     * edited layer no longer has, i.e. a visible discontinuity between layers
     * that are supposed to overlap exactly.
     */
    private diffRing(
        oldCoords: number[][],
        newCoords: number[][],
        toleranceSquared: number,
        movedVertices: Array<{ from: number[]; to: number[] }>,
        insertedVertices: Array<{ edgeStart: number[]; edgeEnd: number[]; vertex: number[] }>,
        removedVertices?: number[][]
    ) {
        if (oldCoords.length === newCoords.length) {
            // Same length — detect moved vertices
            for (let i = 0; i < oldCoords.length; i++) {
                const deltaX = oldCoords[i][0] - newCoords[i][0];
                const deltaY = oldCoords[i][1] - newCoords[i][1];
                if (deltaX * deltaX + deltaY * deltaY > toleranceSquared) {
                    movedVertices.push({ from: oldCoords[i], to: newCoords[i] });
                }
            }
        } else if (newCoords.length > oldCoords.length) {
            // Vertex(es) inserted — find new vertices not present in old ring.
            // Use a two-pointer approach matching old vertices in new ring.
            const matched = new Set<number>();
            let oldIndex = 0;
            for (let newIndex = 0; newIndex < newCoords.length && oldIndex < oldCoords.length; newIndex++) {
                const deltaX = newCoords[newIndex][0] - oldCoords[oldIndex][0];
                const deltaY = newCoords[newIndex][1] - oldCoords[oldIndex][1];
                if (deltaX * deltaX + deltaY * deltaY <= toleranceSquared) {
                    matched.add(newIndex);
                    oldIndex++;
                }
            }
            // Unmatched indices in newCoords are inserted vertices
            for (let newIndex = 0; newIndex < newCoords.length; newIndex++) {
                if (!matched.has(newIndex)) {
                    // Determine the edge (previous matched and next matched)
                    let prevMatchedCoord: number[] | null = null;
                    let nextMatchedCoord: number[] | null = null;
                    for (let backward = newIndex - 1; backward >= 0; backward--) {
                        if (matched.has(backward)) { prevMatchedCoord = newCoords[backward]; break; }
                    }
                    for (let forward = newIndex + 1; forward < newCoords.length; forward++) {
                        if (matched.has(forward)) { nextMatchedCoord = newCoords[forward]; break; }
                    }
                    if (prevMatchedCoord && nextMatchedCoord) {
                        insertedVertices.push({
                            edgeStart: prevMatchedCoord,
                            edgeEnd: nextMatchedCoord,
                            vertex: newCoords[newIndex]
                        });
                    }
                }
            }
        } else if (removedVertices) {
            // Vertex(es) deleted. Detection is deliberately ORDER-INDEPENDENT: a
            // positional two-pointer walk (the mirror of the insertion case
            // above) desynchronises as soon as the gesture was not a pure
            // deletion — a vertex moved at the same time, or the ring got
            // re-anchored because its first corner was the one removed — and from
            // the point of desync it reports every remaining vertex as deleted.
            // Propagating that wipes almost the whole linked ring.
            const openOld = this.openRingView(oldCoords);
            const openNew = this.openRingView(newCoords);

            const stillPresent = (coordinate: number[]): boolean =>
                openNew.some(candidate =>
                    this.squaredEuclideanDistance(candidate, coordinate) <= toleranceSquared);

            const missing = openOld.filter(oldVertex => !stillPresent(oldVertex));

            // Only an UNAMBIGUOUS pure deletion is propagated: the number of
            // vanished coordinates must match the length change exactly. Any
            // other diff (delete + move, reordering) cannot be faithfully
            // expressed as "remove these coordinates", so nothing is propagated
            // rather than risk corrupting the linked geometry.
            if (missing.length === openOld.length - openNew.length) {
                removedVertices.push(...missing);
            }
        }
    }

    /**
     * A ring without its closing duplicate. Deletions must be counted over
     * distinct corners: the closing coordinate repeats the first one, so leaving
     * it in makes removing the first corner look like two deletions.
     */
    private openRingView(coordinates: number[][]): number[][] {
        if (coordinates.length > 1 &&
            this.squaredEuclideanDistance(coordinates[0], coordinates[coordinates.length - 1]) <= 1e-12) {
            return coordinates.slice(0, coordinates.length - 1);
        }
        return coordinates;
    }

    private applyVertexMoves(
        feature: Feature,
        moves: Array<{ from: number[]; to: number[] }>,
        toleranceSquared: number
    ) {
        const geometry = feature.getGeometry();
        if (!geometry) return;

        let mutated = false;

        if (geometry.getType() === 'Polygon' || geometry.getType() === 'MultiPolygon') {
            const rings = this.getGeometryRings(geometry)!;
            for (const ring of rings) {
                for (let i = 0; i < ring.length; i++) {
                    for (const move of moves) {
                        const deltaX = ring[i][0] - move.from[0];
                        const deltaY = ring[i][1] - move.from[1];
                        if (deltaX * deltaX + deltaY * deltaY <= toleranceSquared) {
                            ring[i] = [...move.to];
                            mutated = true;
                            break;
                        }
                    }
                }
            }
            if (mutated) this.setGeometryRings(geometry, rings);
        } else if (geometry.getType() === 'LineString') {
            const coords = (geometry as LineString).getCoordinates();
            for (let i = 0; i < coords.length; i++) {
                for (const move of moves) {
                    const deltaX = coords[i][0] - move.from[0];
                    const deltaY = coords[i][1] - move.from[1];
                    if (deltaX * deltaX + deltaY * deltaY <= toleranceSquared) {
                        coords[i] = [...move.to];
                        mutated = true;
                        break;
                    }
                }
            }
            if (mutated) (geometry as LineString).setCoordinates(coords);
        } else if (geometry.getType() === 'Point') {
            const coord = (geometry as Point).getCoordinates();
            for (const move of moves) {
                const deltaX = coord[0] - move.from[0];
                const deltaY = coord[1] - move.from[1];
                if (deltaX * deltaX + deltaY * deltaY <= toleranceSquared) {
                    (geometry as Point).setCoordinates([...move.to]);
                    mutated = true;
                    break;
                }
            }
        }
    }

    /**
     * Drop vertices from a linked feature that were DELETED on the edited
     * feature, so completely-overlapping layers keep identical outlines.
     *
     * Refuses to destroy geometry: a polygon ring is never reduced below a
     * triangle and a line never below two coordinates. Ring closure is
     * preserved — the closing duplicate is detached before filtering and
     * re-appended afterwards, so deleting the first corner re-closes correctly
     * instead of leaving an open ring.
     */
    private applyVertexRemovals(
        feature: Feature,
        removals: number[][],
        toleranceSquared: number
    ) {
        const geometry = feature.getGeometry();
        if (!geometry) return;

        const isRemoved = (coordinate: number[]): boolean =>
            removals.some(removed =>
                this.squaredEuclideanDistance(coordinate, removed) <= toleranceSquared);

        let mutated = false;

        if (geometry.getType() === 'Polygon' || geometry.getType() === 'MultiPolygon') {
            const rings = this.getGeometryRings(geometry)!;
            const updatedRings = rings.map(ring => {
                const closed = ring.length > 1 &&
                    this.squaredEuclideanDistance(ring[0], ring[ring.length - 1]) <= 1e-12;
                const corners = closed ? ring.slice(0, ring.length - 1) : ring.slice();
                const kept = corners.filter(corner => !isRemoved(corner));
                if (kept.length === corners.length || kept.length < 3) return ring;
                mutated = true;
                return closed ? [...kept, [kept[0][0], kept[0][1]]] : kept;
            });
            if (mutated) this.setGeometryRings(geometry, updatedRings);
        } else if (geometry.getType() === 'LineString') {
            const coords = (geometry as LineString).getCoordinates();
            const kept = coords.filter(coordinate => !isRemoved(coordinate));
            if (kept.length !== coords.length && kept.length >= 2) {
                (geometry as LineString).setCoordinates(kept);
                mutated = true;
            }
        }
        // A Point carries a single coordinate — deleting it would destroy the
        // feature, so points are deliberately left untouched.

        if (mutated) this.updateMeasurement(feature, false);
    }

    /**
     * Insert vertices into the linked feature's geometry on edges that match
     * the insertion edge (edgeStart→edgeEnd) within tolerance.
     */
    private applyVertexInsertions(
        feature: Feature,
        insertions: Array<{ edgeStart: number[]; edgeEnd: number[]; vertex: number[] }>,
        toleranceSquared: number
    ) {
        const geometry = feature.getGeometry();
        if (!geometry) return;

        const matchesEdge = (coordA: number[], coordB: number[], edgeStart: number[], edgeEnd: number[]): boolean => {
            const dxA = coordA[0] - edgeStart[0];
            const dyA = coordA[1] - edgeStart[1];
            const dxB = coordB[0] - edgeEnd[0];
            const dyB = coordB[1] - edgeEnd[1];
            return (dxA * dxA + dyA * dyA <= toleranceSquared) &&
                   (dxB * dxB + dyB * dyB <= toleranceSquared);
        };

        if (geometry.getType() === 'Polygon' || geometry.getType() === 'MultiPolygon') {
            const rings = this.getGeometryRings(geometry)!;
            let mutated = false;
            for (let ringIdx = 0; ringIdx < rings.length; ringIdx++) {
                const ring = rings[ringIdx];
                for (const insertion of insertions) {
                    for (let i = 0; i < ring.length - 1; i++) {
                        if (matchesEdge(ring[i], ring[i + 1], insertion.edgeStart, insertion.edgeEnd)) {
                            ring.splice(i + 1, 0, [...insertion.vertex]);
                            mutated = true;
                            break;
                        }
                    }
                }
                rings[ringIdx] = ring;
            }
            if (mutated) this.setGeometryRings(geometry, rings);
        } else if (geometry.getType() === 'LineString') {
            const coords = (geometry as LineString).getCoordinates();
            let mutated = false;
            for (const insertion of insertions) {
                for (let i = 0; i < coords.length - 1; i++) {
                    if (matchesEdge(coords[i], coords[i + 1], insertion.edgeStart, insertion.edgeEnd)) {
                        coords.splice(i + 1, 0, [...insertion.vertex]);
                        mutated = true;
                        break;
                    }
                }
            }
            if (mutated) (geometry as LineString).setCoordinates(coords);
        }
    }

    private getEditStyleName(vectorLayer: LayerType): string {
        const dependencies = vectorLayer.get('styleDependency');
        const defaultStyle = vectorLayer.get('editStyle');

        if (dependencies && Array.isArray(dependencies)) {
            for (const dependency of dependencies) {
                const dependencyLayer = this.GIS.Layer.getLayer(dependency.layer);
                if (dependencyLayer && dependencyLayer.getVisible()) {
                    return dependency['editStyle']
                }
            }
        }

        return defaultStyle;
    }

    private attachDependencyListeners(layer: LayerType) {
        this.clearDependencyListeners();
        const dependencies = layer.get('styleDependency');

        if (dependencies && Array.isArray(dependencies)) {
            dependencies.forEach(dep => {
                const depLayer = this.GIS.Layer.getLayer(dep.layer);
                if (depLayer) {
                    const key = depLayer.on('change:visible', () => {
                        this.refreshActiveStyle(layer);
                    });
                    this.dependencyListenerKeys.push(key);
                }
            });
        }
    }

    private clearDependencyListeners() {
        this.dependencyListenerKeys.forEach(key => {
            unByKey(key);
        });
        this.dependencyListenerKeys = [];
    }

    private refreshActiveStyle(layer: LayerType) {
        if (!this.isEditing()) return;

        const newStyleName = this.getEditStyleName(layer);
        const newStyle = this.GIS.Style.getStyle(newStyleName);

        if (newStyle) {
            if (this.olDrawInteraction) {
                this.olDrawInteraction.getOverlay().setStyle(newStyle);
            }

            if (this.olModifyInteraction) {
                this.olModifyInteraction.getOverlay().setStyle(newStyle);
            }

            this.style = newStyle;
        }
    }

    /**
     * Start drawing feature in a layer.
     * @param name - The layer should be present in the map.
     * @param type - Supports Point, LineString, Polygon, Circle out of {@link https://openlayers.org/en/latest/apidoc/module-ol_geom_GeometryType.html}
     * @param freehand - Freehand drawing if true. Only hold pointer and draw :-)
     * @param callback - Callback returns on 'addfeature' event feature ID. It is possible to get the ID of the feature being drawn before it is actually created in the source. It is also possible to get IDs of features created in batch when drawing with freehand.
     * @param options
     * Type has to be specified otherwise it tries to guess it from the first feature, when no features are present throws an Error.
     * It automatically calculates the area, length or radius of the feature and set it as a property of the feature.
     * Property the area, length or radius is formatted as 'diameter', 'perimeter,' 'area', 'length' or 'radius' and not formatted to 'cfm_area_value', 'cfm_length_value', 'cfm_radius_value', 'cfm_diameter_value', 'cfm_perimeter_value'.
     * @example GIS.Edit.drawFeatures('point'); //use only when some features are already in layer !!!
     * @example GIS.Edit.drawFeatures('line'); //use only when some features are already in layer !!!
     * @example GIS.Edit.drawFeatures('polygon'); //use only when some features are already in layer !!!
     * @example GIS.Edit.drawFeatures('point', 'Point');
     * @example GIS.Edit.drawFeatures('line', 'LineString');
     * @example GIS.Edit.drawFeatures('polygon', 'Polygon');
     * @example GIS.Edit.drawFeatures('polygon', 'Circle');
     * @throws Cannot recognize feature type.
     * @throws No such layer in the map.
     * @throws Cannot create new feature in this layer.
     */
    public drawFeatures(name: string, type: Type | 'Rectangle', freehand: boolean, callback?: GetIDs, options?: DrawFeaturesOptions) {
        const layer: LayerType = this.GIS.Layer.getLayer(name);

        const resolvedStyleName = this.getEditStyleName(layer);

        if (options && options.attribute) {
            this.idProperty = options.attribute;
        }

        if (options && options.incrementId) {
            this.incrementId = options.incrementId;
        }

        if (options && options.style) {
            this.style = this.GIS.Style.getStyle(options.style);
        } else if (resolvedStyleName) {
            this.style = this.GIS.Style.getStyle(resolvedStyleName);
        } else {
            this.style = undefined
        }

        if (options && options.showMeasurement !== undefined) {
            this.showMeasurement = options.showMeasurement;
        }

        if (options && options.generalize !== undefined && options.generalize !== false) {
            this.generalizeTolerancePixels = options.generalize === true
                ? Edit.defaultGeneralizeTolerancePixels
                : options.generalize;
        } else {
            this.generalizeTolerancePixels = false;
        }

        this.validateDrawing = options && options.validate !== undefined ? options.validate : false;

        if (layer.get('create') !== true) {
            throw new Error('Cannot create new feature in this layer.');
        }

        this.source = layer.getSource();

        this.deactivateDrawListener();

        options && options.single ? this.single = options.single : this.single = false;

        options && options.stopClick !== undefined ? this.stopClick = options.stopClick : this.stopClick = true;

        this.type = type;

        if (!this.type) {
            try {
                const feature: Feature | undefined = this.source?.getFeatures()[0];
                const geometry = feature?.getGeometry()
                if (geometry) {
                    this.type = geometry.getType();
                }
            } catch {
                throw new Error('Cannot recognize feature type.');
            }
        }

        this.commit();

        this.attachDependencyListeners(layer);

        let geometryFunction;
        if (this.type === 'Rectangle') {
            geometryFunction = createBox();
            this.type = 'Circle';
        }

        if (this.source && this.type) {
            this.createDraw(this.source, this.type, this.style, freehand, geometryFunction, this.stopClick, callback);
        }

        this.addToMap(this.olDrawInteraction);

        //Especially needed for Rectangle - the first point of the click
        this.olDrawInteraction?.on('drawstart', (event) => {
            const feature = event.feature;
            const geometry = feature.getGeometry();

            if (geometry instanceof Polygon) {
                const coords = (geometry as Polygon).getCoordinates();
                if (coords.length > 0 && coords[0].length > 0) {
                    this.sketchAnchor = [...coords[0][0]];
                }
            } else if (geometry instanceof LineString) {
                const coords = (geometry as LineString).getCoordinates();
                if (coords.length > 0) {
                    this.sketchAnchor = [...coords[0]];
                }
            } else if (geometry instanceof Point) {
                const coord = (geometry as Point).getCoordinates();
                this.sketchAnchor = [...coord];
            }
        });

        // A fresh draw session is armed but no vertex has been placed yet.
        // Reset the tracker so this initial 0 is always delivered to a (possibly
        // newly registered) callback, even if the previous session also ended at 0.
        this.lastReportedVertexCount = -1;
        this.emitVertexCount();
    }

    private createDraw(source: VectorSource, type: Type, style: StyleLike | FlatStyle | undefined, freehand: boolean, geometryFunction: GeometryFunction | undefined, stopClick: boolean, callback: GetIDs | undefined) {
        const options: DrawOptionsType = {
            source: source,
            type: type,
            freehand: freehand,
            style: style,
            stopClick: stopClick,
            geometryFunction: undefined,
            finishCondition: () => {
                const overlaySource = this.olDrawInteraction?.getOverlay().getSource();
                const sketchFeature = overlaySource?.getFeatures()[0];
                if (!sketchFeature) return false;

                const geom = sketchFeature.getGeometry();
                if (geom instanceof Polygon) {
                    return geom.getCoordinates()[0].length > 3;
                }
                if (geom instanceof LineString) {
                    return geom.getCoordinates().length > 1;
                }
                return true;
            }
        };

        if (geometryFunction) {
            options['geometryFunction'] = geometryFunction;
        }

        this.olDrawInteraction = new Draw(options);

        this.olDrawInteraction.on('drawstart', (event) => {
            this.drawStart(event)
        });
        this.olDrawInteraction.on('drawend', (event) => {
            this.drawEnd(event, callback)
        });
    }

    private drawStart(evt: DrawEvent) {
        if (this.single && this.source) {
            this.source.clear();
        }

        // set sketch
        const feature = evt.feature;

        feature.getGeometry()?.on('change', () => {
            this.updateMeasurement(feature, true);
            this.emitVertexCount();
        });

        this.emitVertexCount();
    }

    private drawEnd(evt: DrawEvent, callback: GetIDs | undefined) {
        const sketch = evt.feature;

        if (this.incrementId && this.source) {
            this.increment = Math.max(this.increment, highestIntegerId(this.source, this.idProperty));
        }

        const newId = ++this.increment;
        sketch.set(this.idProperty, newId);

        this.generalizeFeature(sketch);

        this.updateMeasurement(sketch, false);

        // Validation runs on the FINAL shape: after generalization, which changes the
        // vertex list, and after the id is assigned, so the report identifies the
        // feature the caller now has.
        this.validateFinishedSketch(sketch, newId);

        this.GIS.Overlay.hide();

        // Feature finished — sketch is gone, report 0 committed vertices.
        // Rely on de-duplication so we never emit two identical values in a row.
        this.emitVertexCount();

        if (callback && this.source) {
            const sourceListener = this.source.on('addfeature', (event) => {
                if (event.feature === sketch) {
                    callback(newId);

                    unByKey(sourceListener);
                }
            });
        }
    }

    /**
     * Generalize (simplify) the finished sketch geometry in place when the
     * `generalize` draw option is set. Freehand drawing emits one vertex per
     * pointer move, so the raw stroke is vertex-heavy; Douglas–Peucker
     * thinning collapses vertices that lie within the tolerance of the line
     * between their neighbors.
     *
     * The configured pixel tolerance is converted to map units with the
     * current view resolution ({@link mapToleranceFromPixels}), which keeps
     * the on-screen smoothness constant across zoom levels. Coordinates are
     * written back onto the existing geometry so the feature identity and any
     * attached listeners are preserved. A guard refuses results that would
     * fall below the minimum valid vertex count (2 for a line, a closed ring
     * of 4 for a polygon), leaving the original geometry untouched.
     */
    private generalizeFeature(feature: Feature): void {
        if (this.generalizeTolerancePixels === false) return;

        const geometry = feature.getGeometry();
        if (!geometry) return;

        const type = geometry.getType();
        if (type !== 'LineString' && type !== 'Polygon' && type !== 'MultiPolygon') return;

        let tolerance: number;
        try {
            tolerance = this.mapToleranceFromPixels(this.generalizeTolerancePixels);
        } catch {
            // No map view available (e.g. headless usage) — nothing to do.
            return;
        }
        if (tolerance <= 0) return;

        // Line strings are thinned with Douglas–Peucker, which keeps the
        // original vertices that matter and drops the rest — the stroke stays
        // smooth. Polygons are simplified ring by ring with the SAME algorithm
        // (see simplifyRingDouglasPeucker); the native Polygon.simplify()
        // instead quantizes coordinates onto a grid, which turns curved
        // freehand outlines into visible stairs.
        if (type === 'LineString') {
            const coordinates = ((geometry as LineString).simplify(tolerance) as LineString).getCoordinates();
            if (coordinates.length >= 2) {
                (geometry as LineString).setCoordinates(coordinates);
            }
        } else if (type === 'Polygon') {
            const rings = (geometry as Polygon).getCoordinates()
                .map(ring => this.simplifyRingDouglasPeucker(ring, tolerance));
            // Outer ring must remain a valid closed ring (>= 4 coordinates).
            if (rings.length > 0 && rings[0].length >= 4) {
                (geometry as Polygon).setCoordinates(rings);
            }
        } else if (type === 'MultiPolygon') {
            const polygons = (geometry as MultiPolygon).getCoordinates()
                .map(rings => rings.map(ring => this.simplifyRingDouglasPeucker(ring, tolerance)));
            if (polygons.length > 0 && polygons.every(rings => rings[0]?.length >= 4)) {
                (geometry as MultiPolygon).setCoordinates(polygons);
            }
        }
    }

    /**
     * Validate a finished sketch and report the outcome, when the `validate` draw
     * option is on (see {@link DrawFeaturesOptions.validate}).
     *
     * This is the library's own "before it is persisted" moment: `drawend` has
     * fired, the feature has its id and its final vertices, and it is about to
     * become a feature of the layer the application will save. Validating here is
     * what keeps a broken polygon — a needle spike, a hair-thin sliver, a
     * self-intersecting boundary — from being stored with a wrong area and no way
     * back.
     *
     * The result goes to the callback registered under `'GeometryValidation'` for
     * every finished feature, valid or not, so one handler can both enable a save
     * button and explain why it stays disabled. An invalid geometry is also warned
     * about on the console, so the defect is visible even without a callback.
     * Nothing is blocked or rewritten — that decision belongs to the application.
     *
     * The check itself is {@link Topology.validateFeature}, so a drawn polygon is
     * judged by exactly the same rules as one already on a layer.
     */
    private validateFinishedSketch(sketch: Feature, id: string | number): void {
        if (this.validateDrawing === false) return;

        const thresholds = typeof this.validateDrawing === 'object' ? this.validateDrawing : undefined;
        const result = this.GIS.Topology.validateFeature(sketch, thresholds);

        if (!result.valid) {
            console.warn(`[Edit.drawFeatures] ${result.message}`);
        }

        if (this.geometryValidationCallback) {
            this.geometryValidationCallback({...result, id});
        }
    }

    /**
     * Simplify a single closed polygon ring with Douglas–Peucker — the same
     * vertex-preserving thinning OpenLayers applies to line strings. We route
     * the ring through a {@link LineString} so the polygon reuses the exact
     * line generalization the user already finds smooth, rather than the
     * grid-quantizing {@link Polygon#simplify} that produces stair-stepped
     * curves.
     *
     * The ring is re-closed after thinning (Douglas–Peucker preserves the
     * first and last coordinates, so a well-formed closed ring stays closed;
     * the guard is defensive). A ring that would collapse below the minimum
     * closed ring of 4 coordinates is returned unchanged, so generalization
     * can never destroy a ring — at worst it leaves that ring as drawn.
     */
    private simplifyRingDouglasPeucker(ring: number[][], tolerance: number): number[][] {
        // A triangle (3 distinct vertices + closing point) is already minimal.
        if (ring.length <= 4) return ring;

        const coordinates = ((new LineString(ring)).simplify(tolerance) as LineString).getCoordinates();
        if (coordinates.length < 4) return ring;

        const first = coordinates[0];
        const last = coordinates[coordinates.length - 1];
        if (first[0] !== last[0] || first[1] !== last[1]) {
            coordinates.push([first[0], first[1]]);
        }
        return coordinates;
    }

    /**
     * Add a vertex to the feature currently being drawn. It is useful when you want to draw a feature by clicking on the map and not following the cursor. It is also useful when you have a separate HTML crosshair in the center of the screen and you want to add a vertex to the center of the map.
     * @param coordinate - The coordinate to add as a vertex. It should be in the same projection as the map. If options.epsg is provided, it can be in a different projection and it will be transformed to the map projection.
     * @param options - Options object with epsg property to specify the projection of the coordinate. If not provided, it is assumed to be in the same projection as the map.
     */
    public addVertex(coordinate: import("ol/coordinate").Coordinate, options?: EPSGOptions) {
        if (!this.olDrawInteraction) return;

        if (options && options.epsg) {
            const transformed = this.GIS.Map.transform(coordinate, options.epsg as ProjectionLike, this.GIS.View.olObject.getProjection());
            coordinate[0] = transformed[0];
            coordinate[1] = transformed[1];
        }

        const overlaySource = this.olDrawInteraction.getOverlay().getSource();
        let sketchFeatures = overlaySource?.getFeatures();

        if (sketchFeatures && sketchFeatures.length === 0) {
            this.sketchAnchor = [...coordinate];
        }

        if (!sketchFeatures || sketchFeatures.length === 0) {
            this.olDrawInteraction.appendCoordinates([coordinate]);
            if (this.type === 'Point') {
                this.finnishSketch();
            }
        } else {
            this.olDrawInteraction.appendCoordinates([coordinate]);
            // For circles sketchFeatures length is 2 and for Rectangle it is 3 because of the way geometry function creates the sketch. In both cases we want to update the sketch coordinate and finnish the sketch on next click.
            if (this.type === 'Circle' && (sketchFeatures.length === 2 || sketchFeatures.length === 3)) {
                this.olDrawInteraction.abortDrawing();
            }

            this.olDrawInteraction.changed();
            this.GIS.Overlay.hide();
        }
    }

    /**
     * Add the center of the map as a vertex to the feature currently being drawn. It is useful when you want to draw a feature by clicking on the map and not following the cursor. It is also useful when you have a separate HTML crosshair in the center of the screen and you want to add a vertex to the center of the map.
     */
    public addMapCenterAsVertex() {
        if (!this.olDrawInteraction) return;

        const center = this.GIS.Map.olObject?.getView().getCenter();
        if (!center) return;

        this.addVertex(center);
    }

    /**
     * Update the coordinate of the sketch currently being drawn to the center of the map. It is not updating a vertex of the final feature, but the sketch feature that is following the cursor. It is useful when you want to draw a feature by clicking on the map and not following the cursor. It is also useful when you have a separate HTML crosshair in the center of the screen and you want to update the sketch coordinate to the center of the map.
     */
    public addMapCenterAsSketchCoordinate() {
        if (!this.olDrawInteraction) return;

        const center = this.GIS.Map.olObject?.getView().getCenter();
        if (!center) return;

        this.updateSketchCoordinate(center);
    }

    /**
     * Update the coordinate of the sketch currently being drawn. It is not updating a vertex of the final feature, but the sketch feature that is following the cursor. It is useful when you want to draw a feature by clicking on the map and not following the cursor. It is also useful when you have a separate HTML crosshair in the center of the screen and you want to update the sketch coordinate to the center of the map.
     * @param coordinate - The coordinate to update the sketch to. It should be in the same projection as the map. If options.epsg is provided, it can be in a different projection and it will be transformed to the map projection.
     * @param options - Options object with epsg property to specify the projection of the coordinate. If not provided, it is assumed to be in the same projection as the map.
     */
    public updateSketchCoordinate(coordinate: number[], options?: EPSGOptions) {
        if (!this.olDrawInteraction) return;

        if (options && options.epsg) {
            const transformed = this.GIS.Map.transform(coordinate, options.epsg as ProjectionLike, this.GIS.View.olObject.getProjection());
            coordinate = [transformed[0], transformed[1]];
        }

        const overlay = this.olDrawInteraction.getOverlay();
        const overlaySource = overlay.getSource();
        const features = overlaySource?.getFeatures();

        if (!features || !overlaySource) return;

        let lastFixedVertex: number[] | null = null;
        let isRectangle = false;
        let rectangleRing: number[][] | null = null;

        features.forEach(feature => {
            const geometry = feature.getGeometry();
            if (!geometry) return;
            const type = geometry.getType();

            if (type === 'Polygon') {
                const poly = geometry as Polygon;
                const rings = poly.getCoordinates();
                const ring = rings[0];

                //Rectangle geometry function creates a ring with 5 coordinates, where the first and the last one are the same. When the sketch is updated, we want to keep the first and the last coordinate the same to keep the rectangle shape, but update the other three coordinates to follow the cursor.
                if (this.type === 'Circle' && ring.length === 5) {
                    isRectangle = true;

                    const start = this.sketchAnchor || ring[0];
                    const end = coordinate;

                    rectangleRing = [
                        start,
                        [end[0], start[1]],
                        end,
                        [start[0], end[1]],
                        start
                    ];

                    poly.setCoordinates([rectangleRing]);
                } else {
                    if (ring.length >= 3) {
                        lastFixedVertex = ring[ring.length - 3];
                        ring[ring.length - 2] = coordinate;
                    } else if (ring.length === 2) {
                        lastFixedVertex = ring[0];
                        ring.splice(1, 0, coordinate);
                    }
                    ring[ring.length - 1] = ring[0];
                    poly.setCoordinates(rings);
                }
            } else if (type === 'Circle') {
                const circle = geometry as Circle;
                const center = circle.getCenter();
                if (center) {
                    const dx = coordinate[0] - center[0];
                    const dy = coordinate[1] - center[1];
                    circle.setRadius(Math.sqrt(dx * dx + dy * dy));
                }
            } else if (type === 'Point') {
                (geometry as Point).setCoordinates(coordinate);
            }
        });

        const lineFeature = features.find(f => f.getGeometry()?.getType() === 'LineString');
        if (lineFeature) {
            const lineGeom = lineFeature.getGeometry() as LineString;

            if (isRectangle && rectangleRing) {
                lineGeom.setCoordinates(rectangleRing);
            } else if (this.type === 'Polygon' && lastFixedVertex) {
                lineGeom.setCoordinates([lastFixedVertex, coordinate]);
            } else if (this.type !== 'Circle') {
                const coords = lineGeom.getCoordinates();
                if (coords.length > 0) {
                    coords[coords.length - 1] = coordinate;
                    lineGeom.setCoordinates(coords);
                }
            }
        }

        overlay.changed();

        if (features.length > 0) {
            const mainFeature = features.find(f => {
                const gType = f.getGeometry()?.getType();
                return gType === 'Polygon' || gType === 'Circle';
            }) || features[0];

            this.updateMeasurement(mainFeature, true);
        }
    }

    /**
     * Register an Edit callback by name, mirroring the dispatch convention used
     * by {@link Map.setCallback} and {@link Interaction.setCallback}.
     *
     * Supported callbacks:
     * - `'VertexCount'` — reports the number of committed vertices of the sketch
     *   currently being drawn. Useful to enable/disable an "undo last vertex" /
     *   "back" button: enable it when `count > 0`, disable it when `count === 0`.
     *   The floating vertex following the cursor is NOT counted. The callback is
     *   invoked only when the committed vertex count actually changes:
     *     - `0` when drawing is armed (no vertex placed yet),
     *     - `0+` while placing / removing vertices,
     *     - `0` again once the feature is finished, aborted or removed.
     *   Works for `LineString` and `Polygon`. For `Point` the count is always
     *   `0` (a point finishes on a single click — nothing to undo).
     * - `'GeometryValidation'` — reports the geometry validity of a feature the
     *   moment it is finished, i.e. right before an application would persist it.
     *   Requires the `validate` draw option (see
     *   {@link DrawFeaturesOptions.validate}); it fires for every finished feature,
     *   valid or not, so one handler can enable a save button and show the reason it
     *   stays disabled. The payload is a {@link GeometryValidationResult} plus the
     *   `id` the feature was given.
     *
     * @param name - Name of the callback to register.
     * @param options - Reserved for future options (currently unused). Pass `{}`
     * or omit it.
     * @param callback - Callback to invoke. Pass `undefined` to remove the
     * previously registered callback.
     * @example
     * GIS.Edit.setCallback('VertexCount', {}, (count) => {
     *     backButton.disabled = count === 0;
     * });
     * GIS.Edit.drawFeatures('polygon', 'Polygon', false);
     * @example
     * // Refuse to save a drawn purchase area whose geometry is broken, and say why.
     * GIS.Edit.setCallback('GeometryValidation', {}, ({valid, message, id}) => {
     *     saveButton.disabled = !valid;
     *     statusLine.textContent = valid ? '' : `Area ${id}: ${message}`;
     * });
     * GIS.Edit.drawFeatures('purchase_area', 'Polygon', false, undefined, {validate: true});
     */
    public setCallback(name: 'VertexCount', options?: VertexCountOptions, callback?: VertexCountCallback): void;
    public setCallback(name: 'GeometryValidation', options?: VertexCountOptions, callback?: GeometryValidationCallback): void;
    public setCallback(name: string, _options?: VertexCountOptions, callback?: VertexCountCallback | GeometryValidationCallback): void {
        switch (name) {
            case 'VertexCount':
                this.vertexCountCallback = callback as VertexCountCallback | undefined;
                break;
            case 'GeometryValidation':
                this.geometryValidationCallback = callback as GeometryValidationCallback | undefined;
                break;
            default:
                console.warn(`[Edit.setCallback] Unknown callback name: ${name}`);
        }
    }

    /**
     * Get the number of committed vertices of the sketch currently being drawn.
     * The floating vertex that follows the cursor is NOT counted. Returns `0`
     * when nothing is being drawn, when drawing a `Point`, or for
     * `Circle` / `Rectangle` geometries (no per-vertex undo).
     *
     * @returns Number of committed vertices of the active sketch.
     * @example
     * if (GIS.Edit.getVertexCount() > 0) GIS.Edit.removeLastPoint();
     */
    public getVertexCount(): number {
        if (!this.olDrawInteraction) return 0;

        const features = this.olDrawInteraction.getOverlay().getSource()?.getFeatures() ?? [];
        if (features.length === 0) return 0;

        let geometry: Geometry | undefined;
        if (this.type === 'Polygon') {
            geometry = features.find(feature => feature.getGeometry()?.getType() === 'Polygon')?.getGeometry();
        } else if (this.type === 'LineString') {
            geometry = features.find(feature => feature.getGeometry()?.getType() === 'LineString')?.getGeometry();
        } else {
            return 0;
        }

        if (!geometry) return 0;
        return this.countSketchVertices(geometry);
    }

    private countSketchVertices(geometry: Geometry): number {
        const type = geometry.getType();

        if (type === 'LineString') {
            return Math.max(0, (geometry as LineString).getCoordinates().length - 1);
        }

        if (type === 'Polygon') {
            const ring = (geometry as Polygon).getCoordinates()[0] ?? [];
            if (ring.length < 2) return 0;
            return Math.max(1, ring.length - 2);
        }
        return 0;
    }

    private emitVertexCount(): void {
        if (!this.vertexCountCallback) return;
        const count = this.getVertexCount();
        if (count !== this.lastReportedVertexCount) {
            this.lastReportedVertexCount = count;
            this.vertexCountCallback(count);
        }
    }

    /**
     * Remove last point of the feature currently being drawn. Does not do anything when drawing POINT or MULTI_POINT geometries.
     * {@link https://openlayers.org/en/latest/apidoc/module-ol_interaction_Draw-Draw.html#removeLastPoint}
     */
    public removeLastPoint() {
        if (!this.olDrawInteraction) {
            throw new Error('No layer is currently edited.');
        }

        const features = this.olDrawInteraction.getOverlay().getSource()?.getFeatures();

        let length;
        //Only for LineString and Polygon
        //Remove hanging overlay
        if (features && features.length > 0) {
            switch (this.type) {
                case 'LineString':
                    length = features[0].getGeometry().getCoordinates().length;
                    break;
                case 'Polygon':
                    length = features[1].getGeometry().getCoordinates().length;
                    break;
            }
        }

        this.olDrawInteraction.removeLastPoint();

        if (length === 2) {
            this.GIS.Overlay.hide();
        }

        // Report the new committed-vertex count after the removal. When the
        // last remaining point is removed OpenLayers aborts the sketch, so the
        // count drops back to 0 and the callback is notified accordingly.
        this.emitVertexCount();
    }

    private deactivateDrawListener() {
        if (this.addFeatureListener) {
            unByKey(this.addFeatureListener);
        }
    }

    /**
     * Finnish current drawing of a feature
     */

    /**
     * Finish current drawing.
     * Robustly handles auto-finishing geometries (Circle/Box) to prevent double-add errors.
     */
    /**
     * Finish current drawing.
     * Forcefully cleans up the overlay if the geometry was already added (auto-finished).
     */
    public finnishSketch() {
        if (!this.olDrawInteraction) return;

        // Refuse to finish a sketch that does not meet the minimum vertex
        // requirement: a Line needs at least 2 points and an Area (Polygon) at
        // least 3. getVertexCount() reports the committed vertices only — the
        // floating vertex following the cursor is excluded — so the check is
        // independent of how the sketch was built (mouse clicks or programmatic
        // addVertex). The sketch is left intact rather than silently discarded,
        // and the shortfall is surfaced as an error so the consuming
        // application can inform the user and let them add the missing points.
        if (this.type === 'LineString' || this.type === 'Polygon') {
            const minimumVertices = this.type === 'Polygon' ? 3 : 2;
            const committedVertices = this.getVertexCount();
            if (committedVertices > 0 && committedVertices < minimumVertices) {
                throw new Error(
                    this.type === 'Polygon'
                        ? `An area must have at least ${minimumVertices} points to be finished.`
                        : `A line must have at least ${minimumVertices} points to be finished.`
                );
            }
        }

        const overlaySource = this.olDrawInteraction.getOverlay().getSource();

        try {
            this.olDrawInteraction.finishDrawing();
        } catch (e) {
            this.olDrawInteraction.abortDrawing();
        }

        if (overlaySource) {
            overlaySource.clear();
        }
        this.GIS.Overlay.hide();

        // Sketch finished/cleared — report 0 committed vertices.
        // A normal finishDrawing() already emitted 0 via drawEnd; rely on the
        // de-duplication in emitVertexCount() so the abort path (which does not
        // fire drawEnd) still reports 0 without producing a duplicate.
        this.emitVertexCount();
    }

    /**
     * Remove the current drawing of a feature.
     * It finishes the drawing and clears the source.
     * It also hides the overlay.
     */
    public removeDrawing() {
        if (this.olDrawInteraction) {
            this.olDrawInteraction.abortDrawing();

            if (this.source) {
                this.source.clear();
            }

            this.GIS.Overlay.hide();

            this.removeFromMap(this.olDrawInteraction);
            delete this.olDrawInteraction;

            // Drawing removed — report 0 committed vertices (de-duplicated).
            this.emitVertexCount();
        }
    }

    /**
     * Check if any layer is currently being edited (drawing or modification)
     */
    public isEditing(): boolean {
        return !!(this.olDrawInteraction || this.olModifyInteraction);
    }
}

export default Edit;
